import React, { useState, useRef, useEffect, useCallback } from 'react';
import './SvgEditor.css';

const SvgEditor = ({ initialSvg, onSave, onCancel }) => {
  const [elements, setElements] = useState([]);
  const [history, setHistory] = useState([[]]);
  const [historyIndex, setHistoryIndex] = useState(0);

  const [selectedIds, setSelectedIds] = useState([]);
  const [hoveredId, setHoveredId] = useState(null);
  const [tool, setTool] = useState('pencil');
  const [isDrawing, setIsDrawing] = useState(false);
  const [resizing, setResizing] = useState({ active: false, handle: null, originalElements: null, originalBB: null });
  const [rotating, setRotating] = useState({ active: false, startAngle: 0, initialRotation: 0 });
  const [dragging, setDragging] = useState({ active: false, startX: 0, startY: 0 });
  const [marquee, setMarquee] = useState(null);
  const [tempElement, setTempElement] = useState(null);

  const [color, setColor] = useState('#000000');
  const [fillColor, setFillColor] = useState('transparent');
  const [penWidth, setPenWidth] = useState(2);
  const [isDashed, setIsDashed] = useState(false);
  const [showFill, setShowFill] = useState(false);
  const [showGrid, setShowGrid] = useState(true);
  const [polylinePoints, setPolylinePoints] = useState(null);
  const [curvePoints, setCurvePoints] = useState(null);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isCircuitExpanded, setIsCircuitExpanded] = useState(true);
  const [isMathExpanded, setIsMathExpanded] = useState(false);

  // States and refs for Math LaTeX keyboard dragging and formula input focus
  const [keyboardOffset, setKeyboardOffset] = useState({ x: 0, y: 0 });
  const [isDraggingKeyboard, setIsDraggingKeyboard] = useState(false);
  const [isKeyboardInputFocused, setIsKeyboardInputFocused] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const keyboardOffsetRef = useRef({ x: 0, y: 0 });


  const svgRef = useRef(null);
  const clipboardRef = useRef([]);
  const pasteCountRef = useRef(0);

  const parseRawSvg = useCallback((svgNode) => {
    const parsed = [];
    let idCounter = Date.now();
    
    // Find all basic elements
    const shapes = svgNode.querySelectorAll('rect, circle, line, path, text');
    shapes.forEach(shape => {
      // Ignore background grid or highlights
      if (shape.getAttribute('fill') === 'url(#grid)' || shape.getAttribute('stroke') === '#6366f1') {
        return;
      }
      
      const type = shape.tagName.toLowerCase();
      const id = shape.getAttribute('id') || `parsed-${idCounter++}`;
      const stroke = shape.getAttribute('stroke') || '#cbd5e1';
      const strokeWidth = parseFloat(shape.getAttribute('stroke-width')) || 2;
      const fill = shape.getAttribute('fill') || 'transparent';
      const isDashed = shape.getAttribute('stroke-dasharray') && shape.getAttribute('stroke-dasharray') !== 'none';
      
      // Parse transform rotate if present, e.g. rotate(angle, cx, cy)
      let rotate = 0;
      const transform = shape.getAttribute('transform') || '';
      const rotateMatch = transform.match(/rotate\(([^, ]+)/);
      if (rotateMatch) {
        rotate = parseFloat(rotateMatch[1]) || 0;
      }

      if (type === 'rect') {
        parsed.push({
          id, type: 'rect',
          x: parseFloat(shape.getAttribute('x')) || 0,
          y: parseFloat(shape.getAttribute('y')) || 0,
          width: parseFloat(shape.getAttribute('width')) || 0,
          height: parseFloat(shape.getAttribute('height')) || 0,
          stroke, strokeWidth, fill, rotate, isDashed
        });
      } else if (type === 'circle') {
        parsed.push({
          id, type: 'circle',
          cx: parseFloat(shape.getAttribute('cx')) || 0,
          cy: parseFloat(shape.getAttribute('cy')) || 0,
          r: parseFloat(shape.getAttribute('r')) || 0,
          stroke, strokeWidth, fill, rotate, isDashed
        });
      } else if (type === 'line') {
        parsed.push({
          id, type: 'line',
          x1: parseFloat(shape.getAttribute('x1')) || 0,
          y1: parseFloat(shape.getAttribute('y1')) || 0,
          x2: parseFloat(shape.getAttribute('x2')) || 0,
          y2: parseFloat(shape.getAttribute('y2')) || 0,
          stroke, strokeWidth, rotate, isDashed
        });
      } else if (type === 'path') {
        const subtype = shape.getAttribute('data-subtype') || null;
        parsed.push({
          id, type: 'path',
          d: shape.getAttribute('d'),
          stroke, strokeWidth, fill, rotate, isDashed, subtype
        });
      } else if (type === 'text') {
        parsed.push({
          id, type: 'text',
          x: parseFloat(shape.getAttribute('x')) || 0,
          y: parseFloat(shape.getAttribute('y')) || 0,
          text: shape.textContent || '',
          fontSize: parseFloat(shape.getAttribute('font-size')) || 16,
          stroke, rotate
        });
      }
    });
    
    return parsed;
  }, []);

  useEffect(() => {
    if (initialSvg) {
      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(initialSvg, 'text/html');
        const svgNode = doc.querySelector('svg');
        if (svgNode) {
          const elementsMeta = svgNode.getAttribute('data-elements');
          let parsed = null;
          if (elementsMeta) {
            try {
              parsed = JSON.parse(elementsMeta);
            } catch (jsonErr) {
              console.warn('Failed to parse data-elements JSON metadata:', jsonErr);
            }
          }
          
          if (Array.isArray(parsed) && parsed.length > 0) {
            setElements(parsed);
            setHistory([parsed]);
            setHistoryIndex(0);
          } else {
            // Fall back cleanly to parsing the raw DOM nodes so shapes are always loaded!
            const rawParsed = parseRawSvg(svgNode);
            if (rawParsed.length > 0) {
              setElements(rawParsed);
              setHistory([rawParsed]);
              setHistoryIndex(0);
            }
          }
        }
      } catch (err) {
        console.error('Failed to parse initial SVG:', err);
      }
    }
  }, [initialSvg, parseRawSvg]);

  // Set default initial color based on active dark theme on mount
  useEffect(() => {
    const isDark = document.body.classList.contains('theme-midnight');
    setColor(isDark ? '#ffffff' : '#000000');
  }, []);

  const pushHistory = useCallback((newElements) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(newElements);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
    setElements(newElements);
  }, [history, historyIndex]);

  // Synchronize color settings with selected element
  useEffect(() => {
    if (selectedIds.length === 1) {
      const el = elements.find(e => e.id === selectedIds[0]);
      if (el) {
        if (el.stroke) setColor(el.stroke);
        if (el.strokeWidth) setPenWidth(el.strokeWidth);
        setIsDashed(!!el.isDashed);
        if (el.fill && el.fill !== 'transparent' && el.fill !== 'none') {
          setShowFill(true);
          setFillColor(el.fill);
        } else {
          setShowFill(false);
        }
      }
    }
  }, [selectedIds]);

  // Handle keyboard dragging
  useEffect(() => {
    if (!isDraggingKeyboard) return;

    const handleMouseMove = (e) => {
      const dx = e.clientX - dragStartRef.current.x;
      const dy = e.clientY - dragStartRef.current.y;
      setKeyboardOffset({
        x: keyboardOffsetRef.current.x + dx,
        y: keyboardOffsetRef.current.y + dy
      });
    };

    const handleMouseUp = () => {
      setIsDraggingKeyboard(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingKeyboard]);

  const handleKeyboardDragStart = (e) => {
    if (e.button !== 0) return; // Left click only
    setIsDraggingKeyboard(true);
    dragStartRef.current = { x: e.clientX, y: e.clientY };
    keyboardOffsetRef.current = { ...keyboardOffset };
    e.preventDefault();
  };


  // Synchronously update selected elements when tool settings change
  const updateSelectedElements = useCallback((updates) => {
    if (selectedIds.length === 0) return;
    const updated = elements.map(el => 
      selectedIds.includes(el.id) ? { ...el, ...updates } : el
    );
    pushHistory(updated);
  }, [selectedIds, elements, pushHistory]);

  const handleColorChange = (newVal) => {
    setColor(newVal);
    updateSelectedElements({ stroke: newVal });
  };

  const handlePenWidthChange = (newVal) => {
    setPenWidth(newVal);
    updateSelectedElements({ strokeWidth: newVal });
  };

  const handleShowFillChange = (newChecked) => {
    setShowFill(newChecked);
    const targetFill = newChecked ? (fillColor === 'transparent' || fillColor === 'none' ? '#6366f1' : fillColor) : 'transparent';
    if (newChecked && (fillColor === 'transparent' || fillColor === 'none')) {
      setFillColor('#6366f1'); // Default beautiful indigo fill instead of transparent
    }
    updateSelectedElements({ fill: targetFill });
  };

  const handleFillColorChange = (newVal) => {
    setFillColor(newVal);
    if (showFill) {
      updateSelectedElements({ fill: newVal });
    }
  };

  const handleIsDashedChange = (newChecked) => {
    setIsDashed(newChecked);
    updateSelectedElements({ isDashed: newChecked });
  };

  const translateLatexToUnicode = (text) => {
    if (!text) return "";
    const latexMap = {
      '\\\\alpha': 'α',
      '\\\\beta': 'β',
      '\\\\gamma': 'γ',
      '\\\\delta': 'δ',
      '\\\\Delta': 'Δ',
      '\\\\epsilon': 'ε',
      '\\\\zeta': 'ζ',
      '\\\\eta': 'η',
      '\\\\theta': 'θ',
      '\\\\iota': 'ι',
      '\\\\kappa': 'κ',
      '\\\\lambda': 'λ',
      '\\\\mu': 'μ',
      '\\\\nu': 'ν',
      '\\\\xi': 'ξ',
      '\\\\pi': 'π',
      '\\\\rho': 'ρ',
      '\\\\sigma': 'σ',
      '\\\\tau': 'τ',
      '\\\\upsilon': 'υ',
      '\\\\phi': 'φ',
      '\\\\chi': 'χ',
      '\\\\psi': 'ψ',
      '\\\\omega': 'ω',
      '\\\\Omega': 'Ω',
      '\\\\deg': '°',
      '\\\\degree': '°',
      '\\\\times': '×',
      '\\\\pm': '±',
      '\\\\infty': '∞',
      '\\\\partial': '∂',
      '\\\\int': '∫',
      '\\\\sum': '∑',
      '\\\\sqrt': '√',
      '\\\\angle': '∠'
    };
    let result = text;
    
    // 1. Translate basic LaTeX commands
    Object.entries(latexMap).forEach(([latex, unicode]) => {
      const regex = new RegExp(latex, 'g');
      result = result.replace(regex, unicode);
    });

    // 2. Translate superscripts & subscripts
    const superscripts = {
      '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴', '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹',
      '+': '⁺', '-': '⁻', '=': '⁼', '(': '⁽', ')': '⁾',
      'a': 'ᵃ', 'b': 'ᵇ', 'c': 'ᶜ', 'd': 'ᵈ', 'e': 'ᵉ', 'f': 'ᶠ', 'g': 'ᵍ', 'h': 'ʰ', 'i': 'ⁱ', 'j': 'ʲ',
      'k': 'ᵏ', 'l': 'ˡ', 'm': 'ᵐ', 'n': 'ⁿ', 'o': 'ᵒ', 'p': 'ᵖ', 'r': 'ʳ', 's': 'ˢ', 't': 'ᵗ', 'u': 'ᵘ',
      'v': 'ᵛ', 'w': 'ʷ', 'x': 'ˣ', 'y': 'ʸ', 'z': 'ᶻ'
    };
    const subscripts = {
      '0': '₀', '1': '₁', '2': '₂', '3': '₃', '4': '₄', '5': '₅', '6': '₆', '7': '₇', '8': '₈', '9': '₉',
      '+': '₊', '-': '₋', '=': '₌', '(': '₍', ')': '₎',
      'a': 'ₐ', 'e': 'ₑ', 'h': 'ₕ', 'i': 'ᵢ', 'j': 'ⱼ', 'k': 'ₖ', 'l': 'ₗ', 'm': 'ₘ', 'n': 'ₙ', 'o': 'ₒ',
      'p': 'ₚ', 'r': 'ᵣ', 's': 'ₛ', 't': 'ₜ', 'u': 'ᵤ', 'v': 'ᵥ', 'x': 'ₓ'
    };

    // Replace curly-braced superscripts: ^{2}
    result = result.replace(/\^{([^}]+)}/g, (match, content) => {
      return content.split('').map(char => superscripts[char] || char).join('');
    });
    // Replace single-character superscripts: ^2
    result = result.replace(/\^([a-zA-Z0-9+\-=()])/g, (match, char) => {
      return superscripts[char] || char;
    });

    // Replace curly-braced subscripts: _{0}
    result = result.replace(/_{([^}]+)}/g, (match, content) => {
      return content.split('').map(char => subscripts[char] || char).join('');
    });
    // Replace single-character subscripts: _0
    result = result.replace(/_([a-zA-Z0-9+\-=()])/g, (match, char) => {
      return subscripts[char] || char;
    });

    return result;
  };

  const handleTextChange = (val) => {
    const unicodeVal = translateLatexToUnicode(val);
    updateSelectedElements({ text: unicodeVal });
  };

  const insertSymbol = (code) => {
    const activeEl = document.activeElement;
    if (activeEl && activeEl.className && activeEl.className.includes('keyboard-text-input')) {
      const start = activeEl.selectionStart || 0;
      const end = activeEl.selectionEnd || 0;
      const val = activeEl.value || "";
      const newVal = val.slice(0, start) + code + val.slice(end);
      
      handleTextChange(newVal);

      // Reset selection caret to be after the inserted code
      setTimeout(() => {
        activeEl.focus();
        const newPos = start + code.length;
        activeEl.setSelectionRange(newPos, newPos);
      }, 0);
    } else {
      // Fallback: append to the selected text element
      if (selectedIds.length === 1) {
        const targetEl = elements.find(el => el.id === selectedIds[0]);
        if (targetEl && targetEl.type === 'text') {
          handleTextChange((targetEl.text || "") + code);
          setTimeout(() => {
            const input = document.querySelector('.keyboard-text-input');
            if (input) input.focus();
          }, 0);
        }
      }
    }
  };

  const [editingTextId, setEditingTextId] = useState(null);
  const [editingValue, setEditingValue] = useState("");

  const finalizeCurve = useCallback(() => {
    if (!curvePoints || curvePoints.length < 2) {
      setCurvePoints(null);
      return;
    }

    // Build path using cubic Bezier curves
    let pathData = `M ${curvePoints[0].x} ${curvePoints[0].y}`;
    
    if (curvePoints.length === 2) {
      // Two points: just draw a line
      pathData += ` L ${curvePoints[1].x} ${curvePoints[1].y}`;
    } else {
      // Three or more points: use quadratic Bezier curves
      for (let i = 1; i < curvePoints.length; i++) {
        const controlX = curvePoints[i].x;
        const controlY = curvePoints[i].y;
        if (i < curvePoints.length - 1) {
          const nextX = curvePoints[i + 1].x;
          const nextY = curvePoints[i + 1].y;
          pathData += ` Q ${controlX} ${controlY} ${nextX} ${nextY}`;
          i++; // Skip next point as it's the end point of this curve
        }
      }
    }

    // Calculate bounding box
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    curvePoints.forEach(p => {
      minX = Math.min(minX, p.x);
      minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x);
      maxY = Math.max(maxY, p.y);
    });

    const newEl = {
      id: Date.now(),
      type: 'path',
      subtype: 'curve',
      d: pathData,
      points: curvePoints,
      minX, minY, maxX, maxY,
      stroke: color,
      strokeWidth: penWidth,
      fill: 'none',
      rotate: 0,
      flipX: 1,
      flipY: 1,
      isDashed
    };

    setElements(prev => [...prev, newEl]);
    pushHistory([...elements, newEl]);
    setCurvePoints(null);
  }, [curvePoints, color, penWidth, isDashed, elements, pushHistory]);

  const generateAnglePath = useCallback((x, y, angleDeg) => {
    const rad = (angleDeg * Math.PI) / 180;
    const r = 50; // Ray length
    const arcR = 25; // Arc radius
    const x3 = x + r * Math.cos(-rad);
    const y3 = y + r * Math.sin(-rad);
    const ax = x + arcR;
    const ay = y;
    const bx = x + arcR * Math.cos(-rad);
    const by = y + arcR * Math.sin(-rad);
    const largeArc = angleDeg > 180 ? 1 : 0;
    return `M ${x + r} ${y} L ${x} ${y} L ${x3} ${y3} M ${ax} ${ay} A ${arcR} ${arcR} 0 ${largeArc} 0 ${bx} ${by}`;
  }, []);

  const finishEditingText = useCallback(() => {
    if (!editingTextId) return;
    const unicodeValue = translateLatexToUnicode(editingValue);
    const newElements = elements.map(el => {
      if (el.id === editingTextId) {
        let updated = { ...el, text: unicodeValue || "Label" };
        if (el.isAngle) {
          const val = parseInt(unicodeValue.replace(/[^0-9.-]/g, "")) || 45;
          const ox = el.originX || el.x || 0;
          const oy = el.originY || el.y || 0;
          updated.angleValue = val;
          updated.text = val + "°";
          updated.label = val + "°";
          updated.d = generateAnglePath(ox, oy, val);
        }
        return updated;
      }
      return el;
    });
    setElements(newElements);
    pushHistory(newElements);
    setEditingTextId(null);
    setEditingValue("");
  }, [editingTextId, editingValue, elements, pushHistory, generateAnglePath]);

  const undo = useCallback(() => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setElements(history[newIndex]);
      setSelectedIds([]);
    }
  }, [history, historyIndex]);

  const redo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setElements(history[newIndex]);
      setSelectedIds([]);
    }
  }, [history, historyIndex]);

  const deleteSelected = useCallback(() => {
    if (selectedIds.length > 0) {
      const newElements = elements.filter(el => !selectedIds.includes(el.id));
      pushHistory(newElements);
      setSelectedIds([]);
    }
  }, [elements, selectedIds, pushHistory]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      const isInput = e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable;
      if (isInput) return;

      if (e.key === 'Escape') {
        if (polylinePoints) {
          const finalPoints = polylinePoints.slice(0, -1);
          if (finalPoints.length > 1) {
            let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
            finalPoints.forEach(p => {
              minX = Math.min(minX, p.x); minY = Math.min(minY, p.y);
              maxX = Math.max(maxX, p.x); maxY = Math.max(maxY, p.y);
            });
            const d = `M ${finalPoints[0].x} ${finalPoints[0].y} ` + finalPoints.slice(1).map(p => `L ${p.x} ${p.y}`).join(' ');
            const newEl = {
              id: Date.now(), type: 'path', subtype: 'polyline', d, points: finalPoints,
              minX, minY, maxX, maxY,
              stroke: color, strokeWidth: penWidth, fill: 'none', rotate: 0, flipX: 1, flipY: 1
            };
            setElements(prev => [...prev, newEl]);
            pushHistory([...elements, newEl]);
          }
          setPolylinePoints(null);
        } else if (curvePoints) {
          finalizeCurve();
        } else {
          setSelectedIds([]);
        }
      }

      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        deleteSelected();
      }
      if (e.ctrlKey && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        undo();
      }
      if (e.ctrlKey && (e.key.toLowerCase() === 'y' || (e.shiftKey && e.key.toLowerCase() === 'z'))) {
        e.preventDefault();
        redo();
      }

      // Copy selected elements (Ctrl+C)
      if (e.ctrlKey && e.key.toLowerCase() === 'c' && !e.shiftKey) {
        e.preventDefault();
        if (selectedIds.length > 0) {
          const copied = elements.filter(el => selectedIds.includes(el.id)).map(el => JSON.parse(JSON.stringify(el)));
          clipboardRef.current = copied;
          pasteCountRef.current = 0;
        }
      }

      // Paste copied elements (Ctrl+V)
      if (e.ctrlKey && e.key.toLowerCase() === 'v' && !e.shiftKey) {
        e.preventDefault();
        if (clipboardRef.current.length > 0) {
          pasteCountRef.current += 1;
          const offset = 20 * pasteCountRef.current;
          const pasted = clipboardRef.current.map(el => {
            const newId = Date.now() + Math.random();
            const clone = JSON.parse(JSON.stringify(el));
            clone.id = newId;
            // Offset position based on element type
            if (clone.type === 'rect') { clone.x += offset; clone.y += offset; }
            else if (clone.type === 'circle') { clone.cx += offset; clone.cy += offset; }
            else if (clone.type === 'line') { clone.x1 += offset; clone.y1 += offset; clone.x2 += offset; clone.y2 += offset; }
            else if (clone.type === 'text') { clone.x += offset; clone.y += offset; }
            else if (clone.type === 'path') {
              clone.d = clone.d.replace(/([MLHVCSQTAZmlhvcsqtaz])([^MLHVCSQTAZmlhvcsqtaz]*)/g, (match, cmd, args) => {
                const coords = Array.from(args.matchAll(/[-+]?(?:\d*\.\d+|\d+\.?)(?:[eE][-+]?\d+)?/g)).map(m => parseFloat(m[0]));
                if (coords.length === 0 && cmd.toLowerCase() !== 'z') return match;
                const lowerCmd = cmd.toLowerCase();
                const isAbsolute = cmd === cmd.toUpperCase() && cmd !== 'z';
                let transformedArgs = '';
                const argCounts = { m: 2, l: 2, h: 1, v: 1, c: 6, s: 4, q: 4, t: 2, a: 7, z: 0 };
                const argCount = argCounts[lowerCmd] || 2;
                if (argCount > 0) {
                  for (let i = 0; i < coords.length; i += argCount) {
                    const currentArgs = coords.slice(i, i + argCount);
                    if (currentArgs.length < argCount) break;
                    if (!isAbsolute) { transformedArgs += ` ${currentArgs.join(' ')}`; continue; }
                    if (lowerCmd === 'h') { transformedArgs += ` ${currentArgs[0] + offset}`; }
                    else if (lowerCmd === 'v') { transformedArgs += ` ${currentArgs[0] + offset}`; }
                    else if (lowerCmd === 'a') {
                      transformedArgs += ` ${currentArgs[0]} ${currentArgs[1]} ${currentArgs[2]} ${currentArgs[3]} ${currentArgs[4]} ${currentArgs[5] + offset} ${currentArgs[6] + offset}`;
                    } else {
                      for (let j = 0; j < currentArgs.length; j += 2) {
                        if (j + 1 < currentArgs.length) transformedArgs += ` ${currentArgs[j] + offset} ${currentArgs[j + 1] + offset}`;
                      }
                    }
                  }
                }
                return cmd + transformedArgs;
              });
              if (clone.points) clone.points = clone.points.map(p => ({ x: p.x + offset, y: p.y + offset }));
              if (clone.minX !== undefined) { clone.minX += offset; clone.maxX += offset; }
              if (clone.minY !== undefined) { clone.minY += offset; clone.maxY += offset; }
              if (clone.originX !== undefined) { clone.originX += offset; clone.originY += offset; }
            }
            return clone;
          });
          const newElements = [...elements, ...pasted];
          pushHistory(newElements);
          setSelectedIds(pasted.map(el => el.id));
        }
      }

      // Duplicate in place (Ctrl+D)
      if (e.ctrlKey && e.key.toLowerCase() === 'd') {
        e.preventDefault();
        if (selectedIds.length > 0) {
          const copied = elements.filter(el => selectedIds.includes(el.id)).map(el => JSON.parse(JSON.stringify(el)));
          const pasted = copied.map(el => {
            const newId = Date.now() + Math.random();
            const clone = { ...el, id: newId };
            if (clone.type === 'rect') { clone.x += 20; clone.y += 20; }
            else if (clone.type === 'circle') { clone.cx += 20; clone.cy += 20; }
            else if (clone.type === 'line') { clone.x1 += 20; clone.y1 += 20; clone.x2 += 20; clone.y2 += 20; }
            else if (clone.type === 'text') { clone.x += 20; clone.y += 20; }
            else if (clone.type === 'path') {
              clone.d = clone.d.replace(/([MLHVCSQTAZmlhvcsqtaz])([^MLHVCSQTAZmlhvcsqtaz]*)/g, (match, cmd, args) => {
                const coords = Array.from(args.matchAll(/[-+]?(?:\d*\.\d+|\d+\.?)(?:[eE][-+]?\d+)?/g)).map(m => parseFloat(m[0]));
                if (coords.length === 0 && cmd.toLowerCase() !== 'z') return match;
                const lowerCmd = cmd.toLowerCase();
                const isAbsolute = cmd === cmd.toUpperCase() && cmd !== 'z';
                let transformedArgs = '';
                const argCounts = { m: 2, l: 2, h: 1, v: 1, c: 6, s: 4, q: 4, t: 2, a: 7, z: 0 };
                const argCount = argCounts[lowerCmd] || 2;
                if (argCount > 0) {
                  for (let i = 0; i < coords.length; i += argCount) {
                    const currentArgs = coords.slice(i, i + argCount);
                    if (currentArgs.length < argCount) break;
                    if (!isAbsolute) { transformedArgs += ` ${currentArgs.join(' ')}`; continue; }
                    if (lowerCmd === 'h') { transformedArgs += ` ${currentArgs[0] + 20}`; }
                    else if (lowerCmd === 'v') { transformedArgs += ` ${currentArgs[0] + 20}`; }
                    else if (lowerCmd === 'a') {
                      transformedArgs += ` ${currentArgs[0]} ${currentArgs[1]} ${currentArgs[2]} ${currentArgs[3]} ${currentArgs[4]} ${currentArgs[5] + 20} ${currentArgs[6] + 20}`;
                    } else {
                      for (let j = 0; j < currentArgs.length; j += 2) {
                        if (j + 1 < currentArgs.length) transformedArgs += ` ${currentArgs[j] + 20} ${currentArgs[j + 1] + 20}`;
                      }
                    }
                  }
                }
                return cmd + transformedArgs;
              });
              if (clone.points) clone.points = clone.points.map(p => ({ x: p.x + 20, y: p.y + 20 }));
              if (clone.minX !== undefined) { clone.minX += 20; clone.maxX += 20; }
              if (clone.minY !== undefined) { clone.minY += 20; clone.maxY += 20; }
              if (clone.originX !== undefined) { clone.originX += 20; clone.originY += 20; }
            }
            return clone;
          });
          const newElements = [...elements, ...pasted];
          pushHistory(newElements);
          setSelectedIds(pasted.map(el => el.id));
        }
      }

      // Arrow key movement for selected elements
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
        if (selectedIds.length === 0) return;
        e.preventDefault();
        const step = e.shiftKey ? 10 : 1;
        const dx = e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0;
        const dy = e.key === 'ArrowUp' ? -step : e.key === 'ArrowDown' ? step : 0;

        setElements(prev => {
          const next = prev.map(el => {
            if (!selectedIds.includes(el.id)) return el;
            if (el.type === 'rect') return { ...el, x: el.x + dx, y: el.y + dy };
            if (el.type === 'circle') return { ...el, cx: el.cx + dx, cy: el.cy + dy };
            if (el.type === 'line') return { ...el, x1: el.x1 + dx, y1: el.y1 + dy, x2: el.x2 + dx, y2: el.y2 + dy };
            if (el.type === 'text') return { ...el, x: el.x + dx, y: el.y + dy };
            if (el.type === 'path') {
              const newD = el.d.replace(/([MLHVCSQTAZmlhvcsqtaz])([^MLHVCSQTAZmlhvcsqtaz]*)/g, (match, cmd, args) => {
                const coords = Array.from(args.matchAll(/[-+]?(?:\d*\.\d+|\d+\.?)(?:[eE][-+]?\d+)?/g)).map(m => parseFloat(m[0]));
                if (coords.length === 0 && cmd.toLowerCase() !== 'z') return match;
                const lowerCmd = cmd.toLowerCase();
                const isAbsolute = cmd === cmd.toUpperCase() && cmd !== 'z';
                let transformedArgs = '';
                const argCounts = { m: 2, l: 2, h: 1, v: 1, c: 6, s: 4, q: 4, t: 2, a: 7, z: 0 };
                const argCount = argCounts[lowerCmd] || 2;
                if (argCount > 0) {
                  for (let i = 0; i < coords.length; i += argCount) {
                    const currentArgs = coords.slice(i, i + argCount);
                    if (currentArgs.length < argCount) break;
                    if (!isAbsolute) { transformedArgs += ` ${currentArgs.join(' ')}`; continue; }
                    if (lowerCmd === 'h') { transformedArgs += ` ${currentArgs[0] + dx}`; }
                    else if (lowerCmd === 'v') { transformedArgs += ` ${currentArgs[0] + dy}`; }
                    else if (lowerCmd === 'a') {
                      transformedArgs += ` ${currentArgs[0]} ${currentArgs[1]} ${currentArgs[2]} ${currentArgs[3]} ${currentArgs[4]} ${currentArgs[5] + dx} ${currentArgs[6] + dy}`;
                    } else {
                      for (let j = 0; j < currentArgs.length; j += 2) {
                        if (j + 1 < currentArgs.length) transformedArgs += ` ${currentArgs[j] + dx} ${currentArgs[j + 1] + dy}`;
                      }
                    }
                  }
                }
                return cmd + transformedArgs;
              });
              const newPoints = el.points ? el.points.map(p => ({ x: p.x + dx, y: p.y + dy })) : null;
              let updated = { ...el, d: newD, points: newPoints, minX: el.minX + dx, maxX: el.maxX + dx, minY: el.minY + dy, maxY: el.maxY + dy };
              if (el.isAngle) { updated.originX = (el.originX || 0) + dx; updated.originY = (el.originY || 0) + dy; }
              return updated;
            }
            return el;
          });
          // Push to history on key-up (once per press) to keep undo coarse-grained
          const onKeyUp = () => { pushHistory(next); window.removeEventListener('keyup', onKeyUp); };
          window.addEventListener('keyup', onKeyUp);
          return next;
        });
        return;
      }

      // Tab key to cycle through elements
      if (e.key === 'Tab') {
        e.preventDefault();
        const selectableElements = elements.filter(el => el.type !== 'path' || el.subtype); // Skip raw paths without subtype
        if (selectableElements.length === 0) return;

        let nextIndex = 0;
        if (selectedIds.length === 1) {
          const currentIndex = selectableElements.findIndex(el => el.id === selectedIds[0]);
          if (currentIndex !== -1) {
            nextIndex = e.shiftKey 
              ? (currentIndex - 1 + selectableElements.length) % selectableElements.length
              : (currentIndex + 1) % selectableElements.length;
          }
        }
        setSelectedIds([selectableElements[nextIndex].id]);
        return;
      }

      // Tool shortcuts
      if (e.key === 'v') setTool('select');
      if (e.key === 'p') setTool('pencil');
      if (e.key === 'r') setTool('rect');
      if (e.key === 'c') setTool('circle');
      if (e.key === 'l') setTool('line');
      if (e.key === 'b') setTool('curve');
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo, deleteSelected, polylinePoints, elements, color, penWidth, pushHistory, selectedIds]);

  const getCoordinates = (e) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const CTM = svg.getScreenCTM();
    const clientX = (e.touches ? e.touches[0].clientX : e.clientX);
    const clientY = (e.touches ? e.touches[0].clientY : e.clientY);
    return {
      x: (clientX - CTM.e) / CTM.a,
      y: (clientY - CTM.f) / CTM.d
    };
  };

  const rotatePoint = (px, py, cx, cy, angle) => {
    const rad = (angle * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    const dx = px - cx;
    const dy = py - cy;
    return {
      x: cos * dx - sin * dy + cx,
      y: sin * dx + cos * dy + cy
    };
  };

  const computePathBoundingBox = (d) => {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    const update = (x, y) => {
      if (!isNaN(x)) { minX = Math.min(minX, x); maxX = Math.max(maxX, x); }
      if (!isNaN(y)) { minY = Math.min(minY, y); maxY = Math.max(maxY, y); }
    };

    const matches = d.matchAll(/([MLHVCSQTAmlhvcsqtaz])([^MLHVCSQTAmlhvcsqtaz]*)/g);
    let curX = 0, curY = 0;

    for (const match of matches) {
      const cmd = match[1];
      const coords = Array.from(match[2].matchAll(/[-+]?(?:\d*\.\d+|\d+\.?)(?:[eE][-+]?\d+)?/g)).map(m => parseFloat(m[0]));
      const isAbsolute = cmd === cmd.toUpperCase();
      const lowerCmd = cmd.toLowerCase();

      if (lowerCmd === 'm' || lowerCmd === 'l' || lowerCmd === 't') {
        for (let i = 0; i < coords.length; i += 2) {
          if (isAbsolute) { curX = coords[i]; curY = coords[i+1]; }
          else { curX += coords[i]; curY += coords[i+1]; }
          update(curX, curY);
        }
      } else if (lowerCmd === 'h') {
        for (let i = 0; i < coords.length; i++) {
          if (isAbsolute) curX = coords[i]; else curX += coords[i];
          update(curX, curY);
        }
      } else if (lowerCmd === 'v') {
        for (let i = 0; i < coords.length; i++) {
          if (isAbsolute) curY = coords[i]; else curY += coords[i];
          update(curX, curY);
        }
      } else if (lowerCmd === 'c') {
        for (let i = 0; i < coords.length; i += 6) {
          if (isAbsolute) { curX = coords[i+4]; curY = coords[i+5]; }
          else { curX += coords[i+4]; curY += coords[i+5]; }
          update(curX, curY);
        }
      } else if (lowerCmd === 's' || lowerCmd === 'q') {
        for (let i = 0; i < coords.length; i += 4) {
          if (isAbsolute) { curX = coords[i+2]; curY = coords[i+3]; }
          else { curX += coords[i+2]; curY += coords[i+3]; }
          update(curX, curY);
        }
      } else if (lowerCmd === 'a') {
        for (let i = 0; i < coords.length; i += 7) {
          const rx = coords[i], ry = coords[i+1];
          const dx = coords[i+5], dy = coords[i+6];
          const oldX = curX, oldY = curY;
          if (isAbsolute) { curX = dx; curY = dy; }
          else { curX += dx; curY += dy; }
          update(curX, curY);
          // Simple bulge approximation: check potential extremes of the arc
          update(oldX + rx, oldY); update(oldX - rx, oldY);
          update(oldX, oldY + ry); update(oldX, oldY - ry);
          update(curX + rx, curY); update(curX - rx, curY);
          update(curX, curY + ry); update(curX, curY - ry);
        }
      }
    }
    if (minX === Infinity) return { x: 0, y: 0, width: 50, height: 50, cx: 25, cy: 25 };
    return { x: minX, y: minY, width: maxX - minX, height: maxY - minY, cx: (minX + maxX) / 2, cy: (minY + maxY) / 2 };
  };

  const getBoundingBox = (el) => {
    if (el.minX !== undefined && el.maxX !== undefined && el.minY !== undefined && el.maxY !== undefined) {
      return {
        x: el.minX, y: el.minY,
        width: el.maxX - el.minX, height: el.maxY - el.minY,
        cx: (el.minX + el.maxX) / 2, cy: (el.minY + el.maxY) / 2
      };
    }
    if (el.type === 'rect') return { x: el.x, y: el.y, width: el.width, height: el.height, cx: el.x + el.width / 2, cy: el.y + el.height / 2 };
    if (el.type === 'circle') return { x: el.cx - el.r, y: el.cy - el.r, width: el.r * 2, height: el.r * 2, cx: el.cx, cy: el.cy };
    if (el.type === 'line') return {
      x: Math.min(el.x1, el.x2), y: Math.min(el.y1, el.y2),
      width: Math.abs(el.x1 - el.x2), height: Math.abs(el.y1 - el.y2),
      cx: (el.x1 + el.x2) / 2, cy: (el.y1 + el.y2) / 2
    };
    if (el.type === 'path') return computePathBoundingBox(el.d);
    if (el.type === 'text') {
      const w = el.text.length * (el.fontSize * 0.6);
      const h = el.fontSize;
      return { x: el.x - w / 2, y: el.y - h / 2, width: w, height: h, cx: el.x, cy: el.y };
    }
    return null;
  };

  const getGroupBoundingBox = (ids) => {
    if (ids.length === 0) return null;
    const selectedElements = elements.filter(el => ids.includes(el.id));
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    selectedElements.forEach(el => {
      const bb = getBoundingBox(el);
      if (bb) {
        minX = Math.min(minX, bb.x);
        minY = Math.min(minY, bb.y);
        maxX = Math.max(maxX, bb.x + bb.width);
        maxY = Math.max(maxY, bb.y + bb.height);
      }
    });
    const width = maxX - minX;
    const height = maxY - minY;
    return { x: minX, y: minY, width, height, cx: minX + width / 2, cy: minY + height / 2 };
  };

  const getAllElementsBoundingBox = () => {
    if (elements.length === 0) return null;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    elements.forEach(el => {
      const bb = getBoundingBox(el);
      if (bb) {
        minX = Math.min(minX, bb.x);
        minY = Math.min(minY, bb.y);
        maxX = Math.max(maxX, bb.x + bb.width);
        maxY = Math.max(maxY, bb.y + bb.height);
      }
    });
    if (minX === Infinity || minY === Infinity || maxX === -Infinity || maxY === -Infinity) {
      return null;
    }
    const width = maxX - minX;
    const height = maxY - minY;
    return { x: minX, y: minY, width, height };
  };

  const handleSaveCropped = () => {
    const originalSvg = svgRef.current;
    if (!originalSvg) return;
    const clonedSvg = originalSvg.cloneNode(true);

    // Embed the elements state as JSON metadata on the cloned SVG!
    clonedSvg.setAttribute('data-elements', JSON.stringify(elements));

    const rects = clonedSvg.querySelectorAll('rect');
    rects.forEach(r => {
      if (r.getAttribute('fill') === 'url(#grid)') {
        r.remove();
      }
    });
    const defs = clonedSvg.querySelectorAll('defs');
    defs.forEach(d => {
      if (d.querySelector('#grid')) {
        d.remove();
      }
    });

    clonedSvg.querySelectorAll('[stroke="#6366f1"]').forEach(el => el.remove());
    clonedSvg.querySelectorAll('[data-grab-target="true"]').forEach(el => el.remove());

    // Convert default black stroke/fill to currentColor for automatic light/dark mode visibility
    clonedSvg.querySelectorAll('[stroke="#000000"]').forEach(el => el.setAttribute('stroke', 'currentColor'));
    clonedSvg.querySelectorAll('[stroke="black"]').forEach(el => el.setAttribute('stroke', 'currentColor'));
    clonedSvg.querySelectorAll('[fill="#000000"]').forEach(el => el.setAttribute('fill', 'currentColor'));
    clonedSvg.querySelectorAll('[fill="black"]').forEach(el => el.setAttribute('fill', 'currentColor'));

    clonedSvg.querySelectorAll('circle').forEach(c => {
      if (c.getAttribute('fill') === 'white' && (c.getAttribute('r') === '3' || c.getAttribute('r') === '3.5')) {
        c.remove();
      }
    });

    const bb = getAllElementsBoundingBox();
    const svgRect = svgRef.current ? svgRef.current.getBoundingClientRect() : null;
    const scale = svgRect && svgRect.width > 0 && svgRect.height > 0
      ? Math.min(svgRect.width / 800, svgRect.height / 500)
      : 1.0;

    let cropW = 800;
    let cropH = 500;
    if (bb && bb.width > 0 && bb.height > 0) {
      const padding = 15;
      const cropX = bb.x - padding;
      const cropY = bb.y - padding;
      cropW = bb.width + padding * 2;
      cropH = bb.height + padding * 2;

      clonedSvg.setAttribute('viewBox', `${cropX} ${cropY} ${cropW} ${cropH}`);
      
      const visualWidth = cropW * scale;
      const visualHeight = cropH * scale;
      clonedSvg.setAttribute('width', visualWidth);
      clonedSvg.setAttribute('height', visualHeight);
    } else {
      clonedSvg.setAttribute('viewBox', '0 0 800 500');
      const visualWidth = 800 * scale;
      const visualHeight = 500 * scale;
      clonedSvg.setAttribute('width', visualWidth);
      clonedSvg.setAttribute('height', visualHeight);
    }

    clonedSvg.removeAttribute('style');
    clonedSvg.removeAttribute('class');
    clonedSvg.removeAttribute('overflow');

    // Inline styling for bulletproof cross-browser scale retention support
    if (bb && bb.width > 0 && bb.height > 0) {
      const visualWidth = cropW * scale;
      clonedSvg.style.width = `${visualWidth}px`;
      clonedSvg.style.height = 'auto';
      clonedSvg.style.maxWidth = '100%';
    } else {
      const visualWidth = 800 * scale;
      clonedSvg.style.width = `${visualWidth}px`;
      clonedSvg.style.height = 'auto';
      clonedSvg.style.maxWidth = '100%';
    }

    onSave(clonedSvg.outerHTML);
  };

  const getTerminals = (el) => {
    if (!el.subtype) return [];
    const bb = getBoundingBox(el);
    const { x, y } = bb;

    let localTerminals = [];
    switch (el.subtype) {
      // py = bb.y + (p.y/40)*bb.height = (sy-20) + p.y  =>  to hit sy+offset: p.y = offset+20
      // px = sx + p.x  (bb.width=60, so p.x/60*60 = p.x)
      case 'resistor': case 'potentiometer': case 'capacitor': case 'vcap': case 'battery': case 'diode': case 'led': case 'acsource': case 'dcsource': case 'currentsource': case 'switch': case 'fuse': case 'ammeter': case 'voltmeter':
        localTerminals = [{ x: 0, y: 20 }, { x: 60, y: 20 }];
        break;
      case 'inductor':
        localTerminals = [{ x: 0, y: 20 }, { x: 50, y: 20 }];
        break;
      case 'transformer':
        localTerminals = [{ x: 0, y: 0 }, { x: 0, y: 40 }, { x: 60, y: 0 }, { x: 60, y: 40 }];
        break;
      case 'npn': case 'pnp':
        localTerminals = [{ x: 0, y: 20 }, { x: 50, y: -20 }, { x: 50, y: 60 }];
        break;
      case 'mosfet':
        localTerminals = [{ x: 7, y: 20 }, { x: 40, y: 5 }, { x: 40, y: 35 }];
        break;
      case 'ground':
        localTerminals = [{ x: 30, y: 20 }];
        break;
      case 'connector':
        localTerminals = [{ x: 0, y: 0 }, { x: 60, y: 40 }];
        break;
      case 'polyline':
        return el.points || [];
      default: break;
    }

    const isGate = ['and', 'or', 'nand', 'nor', 'xor', 'xnor'].includes(el.subtype);
    
    if (isGate) {
      localTerminals = [{ x: 0, y: 12 }, { x: 0, y: 28 }, { x: 75, y: 20 }];
    } else if (el.subtype === 'opamp') {
      localTerminals = [{ x: 0, y: 12 }, { x: 0, y: 28 }, { x: 75, y: 20 }];
    } else if (el.subtype === 'not') {
      localTerminals = [{ x: 0, y: 20 }, { x: 75, y: 20 }];
    } else if (el.subtype === 'inductor') {
      localTerminals = [{ x: 0, y: 20 }, { x: 50, y: 20 }];
    } else if (el.subtype === 'transformer') {
      localTerminals = [{ x: 0, y: 0 }, { x: 0, y: 40 }, { x: 60, y: 0 }, { x: 60, y: 40 }];
    } else if (el.subtype === 'npn' || el.subtype === 'pnp') {
      localTerminals = [{ x: 0, y: 20 }, { x: 50, y: -20 }, { x: 50, y: 60 }];
    } else if (el.subtype === 'mosfet') {
      localTerminals = [{ x: 7, y: 20 }, { x: 40, y: 5 }, { x: 40, y: 35 }];
    } else if (el.subtype === 'ground') {
      localTerminals = [{ x: 30, y: 20 }];
    }

    localTerminals = localTerminals.map(p => ({
      x: (p.x / 60) * bb.width,
      y: (p.y / 40) * bb.height
    }));

    return localTerminals.map(p => {
      let px = x + p.x;
      let py = y + p.y;
      
      // Apply Flip
      if (el.flipX === -1) px = bb.cx - (px - bb.cx);
      if (el.flipY === -1) py = bb.cy - (py - bb.cy);

      return rotatePoint(px, py, bb.cx, bb.cy, el.rotate || 0);
    });
  };

  const getSnapPoint = (px, py, excludeIds = []) => {
    const snapDist = 12;
    for (const el of elements) {
      if (excludeIds.includes(el.id)) continue;
      const terminals = getTerminals(el);
      for (const t of terminals) {
        if (Math.abs(px - t.x) < snapDist && Math.abs(py - t.y) < snapDist) {
          return t;
        }
      }
    }
    return null;
  };
  const findTargetAt = (px, py) => {
    const hits = elements.filter(el => {
      const bb = getBoundingBox(el);
      if (!bb) return false;
      const local = rotatePoint(px, py, bb.cx, bb.cy, -(el.rotate || 0));
      const slop = 10;
      return local.x >= bb.x - slop && local.x <= bb.x + bb.width + slop &&
        local.y >= bb.y - slop && local.y <= bb.y + bb.height + slop;
    });
    if (hits.length === 0) return null;
    return hits.sort((a, b) => {
      const bbA = getBoundingBox(a);
      const bbB = getBoundingBox(b);
      return (bbA.width * bbA.height) - (bbB.width * bbB.height);
    })[0];
  };

  const startInteraction = (e) => {
    const { x, y } = getCoordinates(e);

    const groupBB = getGroupBoundingBox(selectedIds);
    const slop = 10;

    // Prioritize existing selection interaction (dragging/resizing/rotating)
    if (groupBB) {
      const handles = getHandles(groupBB);
      const activeHandle = handles.find(h => Math.abs(x - h.x) < 8 && Math.abs(y - h.y) < 8);

      if (activeHandle) {
        if (activeHandle.type === 'rotate') {
          const angle = Math.atan2(y - groupBB.cy, x - groupBB.cx) * (180 / Math.PI);
          setRotating({ active: true, startAngle: angle, initialRotation: elements.find(el => selectedIds.includes(el.id))?.rotate || 0 });
        } else {
          setResizing({
            active: true,
            handle: activeHandle.type,
            originalElements: JSON.parse(JSON.stringify(elements.filter(el => selectedIds.includes(el.id)))),
            originalBB: groupBB
          });
        }
        return;
      }

      // If click is inside the selection box, drag even if a drawing tool is selected
      if (x >= groupBB.x - slop && x <= groupBB.x + groupBB.width + slop &&
        y >= groupBB.y - slop && y <= groupBB.y + groupBB.height + slop) {
        setDragging({ 
          active: true, 
          startX: x, 
          startY: y, 
          originalElements: JSON.parse(JSON.stringify(elements.filter(el => selectedIds.includes(el.id)))) 
        });
        return;
      }

      // If clicked outside, deselect (unless Shift is held)
      if (!e.shiftKey) {
        setSelectedIds([]);
        // We continue to check if we should start a new drawing below
      }
    }

    if (tool === 'connector') {
      const snap = getSnapPoint(x, y);
      const px = snap ? snap.x : x;
      const py = snap ? snap.y : y;

      if (!polylinePoints) {
        setPolylinePoints([{ x: px, y: py }, { x: px, y: py }]); // Start and preview end
      } else {
        const last = polylinePoints[polylinePoints.length - 2];
        // Finish if double click or clicking same point
        if (e.detail === 2 || (Math.abs(px - last.x) < 5 && Math.abs(py - last.y) < 5)) {
          const finalPoints = polylinePoints.slice(0, -1);
          if (finalPoints.length > 1) {
            let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
            finalPoints.forEach(p => {
              minX = Math.min(minX, p.x); minY = Math.min(minY, p.y);
              maxX = Math.max(maxX, p.x); maxY = Math.max(maxY, p.y);
            });
            const d = `M ${finalPoints[0].x} ${finalPoints[0].y} ` + finalPoints.slice(1).map(p => `L ${p.x} ${p.y}`).join(' ');
            const newEl = {
              id: Date.now(), type: 'path', subtype: 'polyline', d, points: finalPoints,
              minX, minY, maxX, maxY,
              stroke: color, strokeWidth: penWidth, fill: 'none', rotate: 0, flipX: 1, flipY: 1
            };
            setElements([...elements, newEl]);
            pushHistory([...elements, newEl]);
          }
          setPolylinePoints(null);
        } else {
          setPolylinePoints([...polylinePoints.slice(0, -1), { x: px, y: py }, { x: px, y: py }]);
        }
      }
      return;
    }

    if (tool === 'curve') {
      const snap = getSnapPoint(x, y);
      const px = snap ? snap.x : x;
      const py = snap ? snap.y : y;

      if (!curvePoints) {
        setCurvePoints([{ x: px, y: py }, { x: px, y: py }]); // Start and preview end
      } else {
        const last = curvePoints[curvePoints.length - 2];
        // Finish if double click or right click
        if (e.detail === 2 || (Math.abs(px - last.x) < 5 && Math.abs(py - last.y) < 5)) {
          finalizeCurve();
        } else {
          setCurvePoints([...curvePoints.slice(0, -1), { x: px, y: py }, { x: px, y: py }]);
        }
      }
      return;
    }

    // Allow selecting elements with any tool if not currently drawing
    if (!polylinePoints && !curvePoints && !isDrawing) {
      const clickedEl = findTargetAt(x, y);
      if (clickedEl) {
        if (!e.ctrlKey) {
          if (!selectedIds.includes(clickedEl.id)) setSelectedIds([clickedEl.id]);
        } else {
          setSelectedIds(prev => prev.includes(clickedEl.id) ? prev.filter(id => id !== clickedEl.id) : [...prev, clickedEl.id]);
        }
        setDragging({ 
          active: true, 
          startX: x, 
          startY: y, 
          originalElements: JSON.parse(JSON.stringify(elements.filter(el => (el.id === clickedEl.id || selectedIds.includes(el.id))))) 
        });
        return;
      }
    }

    if (tool === 'select') {
      if (!e.ctrlKey) setSelectedIds([]);
      setMarquee({ x, y, width: 0, height: 0, startX: x, startY: y });
      return;
    }

    if (tool === 'text') {
      const id = Date.now();
      const newEl = {
        id,
        type: 'text',
        text: "Label",
        x,
        y,
        rotate: 0,
        stroke: color,
        strokeWidth: 2,
        fontSize: 18
      };
      setElements(prev => [...prev, newEl]);
      setSelectedIds([id]);
      setTool('select');
      
      setTimeout(() => {
        const input = document.querySelector('.keyboard-text-input');
        if (input) {
          input.focus();
          input.select();
        }
      }, 50);
      return;
    }

    setIsDrawing(true);
    const id = Date.now();
    const baseEl = {
      id, rotate: 0, flipX: 1, flipY: 1,
      stroke: color, strokeWidth: penWidth,
      fill: showFill ? fillColor : 'transparent', isDashed
    };

    if (tool === 'line') {
      const snap = getSnapPoint(x, y);
      const startX = snap ? snap.x : x;
      const startY = snap ? snap.y : y;
      setTempElement({ ...baseEl, type: 'line', x1: startX, y1: startY, x2: startX, y2: startY, fill: 'transparent' });
    } else if (tool === 'pencil') {
      setTempElement({ ...baseEl, type: 'path', d: `M ${x} ${y}`, minX: x, minY: y, maxX: x, maxY: y });
    } else if (tool === 'rect') {
      setTempElement({ ...baseEl, type: 'rect', x, y, width: 0, height: 0 });
    } else if (tool === 'circle') {
      setTempElement({ ...baseEl, type: 'circle', cx: x, cy: y, r: 0 });
    } else if (['resistor', 'potentiometer', 'capacitor', 'vcap', 'battery', 'ground', 'diode', 'zener', 'led', 'inductor', 'npn', 'pnp', 'opamp', 'and', 'or', 'not', 'acsource', 'dcsource', 'currentsource', 'switch', 'transformer', 'mosfet', 'nand', 'nor', 'xor', 'xnor', 'ammeter', 'voltmeter', 'fuse', 'connector', 'tri_eq', 'tri_right', 'pentagon', 'hexagon', 'angle'].includes(tool)) {
      const snap = getSnapPoint(x, y);
      const sx = snap ? snap.x : x;
      const sy = snap ? snap.y : y;

      const paths = {
        resistor: `M ${sx} ${sy} h 10 l 4 -10 l 8 20 l 8 -20 l 8 20 l 8 -20 l 4 10 h 10`,
        potentiometer: `M ${sx} ${sy} h 10 l 4 -10 l 8 20 l 8 -20 l 8 20 l 8 -20 l 4 10 h 10 M ${sx+15} ${sy+15} l 30 -30 M ${sx+45} ${sy-15} l -5 0 M ${sx+45} ${sy-15} l 0 5`,
        capacitor: `M ${sx} ${sy} h 25 M ${sx + 25} ${sy - 15} v 30 M ${sx + 35} ${sy - 15} v 30 M ${sx + 35} ${sy} h 25`,
        vcap: `M ${sx} ${sy} h 25 M ${sx + 25} ${sy - 15} v 30 M ${sx + 35} ${sy - 15} v 30 M ${sx + 35} ${sy} h 25 M ${sx+15} ${sy+15} l 30 -30 M ${sx+45} ${sy-15} l -5 0 M ${sx+45} ${sy-15} l 0 5`,
        battery: `M ${sx} ${sy} h 25 M ${sx + 25} ${sy - 15} v 30 M ${sx + 35} ${sy - 10} v 20 M ${sx + 35} ${sy} h 25`,
        ground: `M ${sx + 30} ${sy} v 25 M ${sx + 10} ${sy + 25} h 40 M ${sx + 20} ${sy + 32} h 20 M ${sx + 25} ${sy + 39} h 10`,
        diode: `M ${sx} ${sy} h 20 M ${sx + 20} ${sy - 15} v 30 l 20 -15 Z M ${sx + 40} ${sy - 15} v 30 M ${sx + 40} ${sy} h 20`,
        led: `M ${sx} ${sy} h 20 M ${sx + 20} ${sy - 15} v 30 l 20 -15 Z M ${sx + 40} ${sy - 15} v 30 M ${sx + 40} ${sy} h 20 M ${sx + 30} ${sy - 20} l 5 -5 M ${sx + 38} ${sy - 12} l 5 -5`,
        inductor: `M ${sx} ${sy} h 5 a 5 7 0 1 1 8 0 a 5 7 0 1 1 8 0 a 5 7 0 1 1 8 0 a 5 7 0 1 1 8 0 a 5 7 0 1 1 8 0 h 5`,
        npn: `M ${sx+40} ${sy} m -25 0 a 25 25 0 1 0 50 0 a 25 25 0 1 0 -50 0 M ${sx} ${sy} h 25 M ${sx+25} ${sy-15} v 30 M ${sx+25} ${sy-8} L ${sx+50} ${sy-20} v -20 M ${sx+25} ${sy+8} L ${sx+50} ${sy+20} v 20`,
        pnp: `M ${sx+40} ${sy} m -25 0 a 25 25 0 1 0 50 0 a 25 25 0 1 0 -50 0 M ${sx} ${sy} h 25 M ${sx+25} ${sy-15} v 30 M ${sx+25} ${sy-8} L ${sx+50} ${sy-20} v -20 M ${sx+25} ${sy+8} L ${sx+50} ${sy+20} v 20`,
        zener: `M ${sx} ${sy} h 20 M ${sx+20} ${sy-15} v 30 l 20 -15 Z M ${sx+40} ${sy-15} v 30 M ${sx+40} ${sy-15} l -5 0 M ${sx+40} ${sy+15} l 5 0 M ${sx+40} ${sy} h 20`,
        opamp: `M ${sx + 20} ${sy - 15} v 30 l 35 -15 Z M ${sx} ${sy - 8} h 20 M ${sx} ${sy + 8} h 20 M ${sx + 55} ${sy} h 20`,
        and: `M ${sx + 20} ${sy - 15} v 30 h 15 a 15 15 0 0 0 0 -30 Z M ${sx} ${sy - 8} h 20 M ${sx} ${sy + 8} h 20 M ${sx + 50} ${sy} h 25`,
        or: `M ${sx + 20} ${sy - 15} c 5 5 5 25 0 30 c 15 0 30 -7 30 -15 c 0 -8 -15 -15 -30 -15 M ${sx} ${sy - 8} h 20 M ${sx} ${sy + 8} h 20 M ${sx + 50} ${sy} h 25`,
        not: `M ${sx + 20} ${sy - 15} v 30 l 25 -15 Z M ${sx + 48} ${sy} m -3 0 a 3 3 0 1 0 6 0 a 3 3 0 1 0 -6 0 M ${sx} ${sy} h 20 M ${sx + 51} ${sy} h 24`,
        acsource: `M ${sx + 30} ${sy} m -20 0 a 20 20 0 1 0 40 0 a 20 20 0 1 0 -40 0 M ${sx + 22} ${sy} c 4 -10 6 10 10 0 M ${sx} ${sy} h 10 M ${sx + 50} ${sy} h 10`,
        dcsource: `M ${sx+30} ${sy} m -20 0 a 20 20 0 1 0 40 0 a 20 20 0 1 0 -40 0 M ${sx+24} ${sy-6} h 12 M ${sx+30} ${sy-12} v 12 M ${sx+24} ${sy+6} h 12 M ${sx} ${sy} h 10 M ${sx+50} ${sy} h 10`,
        currentsource: `M ${sx+30} ${sy} m -20 0 a 20 20 0 1 0 40 0 a 20 20 0 1 0 -40 0 M ${sx+20} ${sy} h 20 M ${sx+36} ${sy-4} l 4 4 l -4 4 M ${sx} ${sy} h 10 M ${sx+50} ${sy} h 10`,
        switch: `M ${sx} ${sy} h 20 l 20 -15 M ${sx + 40} ${sy} h 20`,
        transformer: `M ${sx + 15} ${sy - 20} v 5 a 5 4.5 0 1 1 0 7.5 a 5 4.5 0 1 1 0 7.5 a 5 4.5 0 1 1 0 7.5 a 5 4.5 0 1 1 0 7.5 v 5 M ${sx + 45} ${sy - 20} v 5 a 5 4.5 0 1 0 0 7.5 a 5 4.5 0 1 0 0 7.5 a 5 4.5 0 1 0 0 7.5 a 5 4.5 0 1 0 0 7.5 v 5 M ${sx + 27} ${sy - 15} v 30 M ${sx + 33} ${sy - 15} v 30 M ${sx} ${sy - 20} h 15 M ${sx} ${sy + 20} h 15 M ${sx + 45} ${sy - 20} h 15 M ${sx + 45} ${sy + 20} h 15`,
        mosfet: `M ${sx + 10} ${sy - 10} v 20 M ${sx + 20} ${sy - 15} v 30 M ${sx + 20} ${sy - 8} h 20 M ${sx + 20} ${sy + 8} h 20 M ${sx + 7} ${sy} h 13 M ${sx + 40} ${sy - 15} v 7 M ${sx + 40} ${sy + 15} v -7`,
        nand: `M ${sx + 20} ${sy - 15} v 30 h 15 a 15 15 0 0 0 0 -30 Z M ${sx + 48} ${sy} m -3 0 a 3 3 0 1 0 6 0 a 3 3 0 1 0 -6 0 M ${sx} ${sy - 8} h 20 M ${sx} ${sy + 8} h 20 M ${sx + 54} ${sy} h 21`,
        nor: `M ${sx + 20} ${sy - 15} c 5 5 5 25 0 30 c 15 0 30 -7 30 -15 c 0 -8 -15 -15 -30 -15 M ${sx + 48} ${sy} m -3 0 a 3 3 0 1 0 6 0 a 3 3 0 1 0 -6 0 M ${sx} ${sy - 8} h 20 M ${sx} ${sy + 8} h 20 M ${sx + 54} ${sy} h 21`,
        xor: `M ${sx + 15} ${sy - 15} c 5 5 5 25 0 30 M ${sx + 20} ${sy - 15} c 5 5 5 25 0 30 c 15 0 30 -7 30 -15 c 0 -8 -15 -15 -30 -15 M ${sx} ${sy - 8} h 20 M ${sx} ${sy + 8} h 20 M ${sx + 50} ${sy} h 25`,
        xnor: `M ${sx+15} ${sy-15} c 5 5 5 25 0 30 M ${sx+20} ${sy-15} c 5 5 5 25 0 30 c 15 0 30 -7 30 -15 c 0 -8 -15 -15 -30 -15 M ${sx+48} ${sy} m -3 0 a 3 3 0 1 0 6 0 a 3 3 0 1 0 -6 0 M ${sx} ${sy-8} h 20 M ${sx} ${sy+8} h 20 M ${sx+54} ${sy} h 21`,
        ammeter: `M ${sx + 30} ${sy} m -15 0 a 15 15 0 1 0 30 0 a 15 15 0 1 0 -30 0 M ${sx} ${sy} h 15 M ${sx + 45} ${sy} h 15`,
        voltmeter: `M ${sx + 30} ${sy} m -15 0 a 15 15 0 1 0 30 0 a 15 15 0 1 0 -30 0 M ${sx} ${sy} h 15 M ${sx + 45} ${sy} h 15`,
        fuse: `M ${sx} ${sy} h 15 M ${sx + 15} ${sy - 5} h 30 v 10 h -30 Z M ${sx + 45} ${sy} h 15`,
        connector: `M ${sx} ${sy} h 30 v 40 h 30`,
        tri_eq: `M ${sx + 10} ${sy + 17.3} L ${sx + 50} ${sy + 17.3} L ${sx + 30} ${sy - 17.3} Z`,
        tri_right: `M ${sx + 10} ${sy + 20} L ${sx + 50} ${sy + 20} L ${sx + 10} ${sy - 20} Z`,
        pentagon: `M ${sx + 30} ${sy - 20} L ${sx + 49} ${sy - 6} L ${sx + 42} ${sy + 16} L ${sx + 18} ${sy + 16} L ${sx + 11} ${sy - 6} Z`,
        hexagon: `M ${sx + 20} ${sy - 17.3} L ${sx + 40} ${sy - 17.3} L ${sx + 50} ${sy} L ${sx + 40} ${sy + 17.3} L ${sx + 20} ${sy + 17.3} L ${sx + 10} ${sy} Z`,
        angle: `M ${sx+50} ${sy} L ${sx} ${sy} L ${sx+35} ${sy-35} M ${sx+20} ${sy} A 20 20 0 0 0 ${sx+14} ${sy-14}`
      };

      const minX = sx, minY = (tool === 'connector' ? sy : sy - 20);
      const maxX = sx + 60, maxY = (tool === 'connector' ? sy + 40 : sy + 20);
      
      let label = null;
      if (tool === 'ammeter') label = 'A';
      if (tool === 'voltmeter') label = 'V';
      if (tool === 'angle') label = '45°';

      setTempElement({
        ...baseEl,
        type: 'path',
        subtype: tool,
        d: paths[tool],
        label,
        isAngle: tool === 'angle',
        angleValue: tool === 'angle' ? 45 : 0,
        originX: sx,
        originY: sy,
        minX, minY, maxX, maxY
      });
      setIsDrawing(false);
      pushHistory([...elements, { 
        ...baseEl, 
        type: 'path', 
        subtype: tool, 
        d: paths[tool], 
        label, 
        isAngle: tool === 'angle',
        angleValue: tool === 'angle' ? 45 : 0,
        originX: sx,
        originY: sy,
        minX, minY, maxX, maxY 
      }]);
      setTool('select');
    }
  };

  const interact = (e) => {
    const { x, y } = getCoordinates(e);

    if (polylinePoints) {
      const snap = getSnapPoint(x, y);
      const px = snap ? snap.x : x;
      const py = (snap && tool !== 'pencil') ? snap.y : y;
      const newPoints = [...polylinePoints];
      newPoints[newPoints.length - 1] = { x: px, y: py };
      setPolylinePoints(newPoints);
      return;
    }

    if (curvePoints) {
      const snap = getSnapPoint(x, y);
      const px = snap ? snap.x : x;
      const py = (snap && tool !== 'pencil') ? snap.y : y;
      const newPoints = [...curvePoints];
      newPoints[newPoints.length - 1] = { x: px, y: py };
      setCurvePoints(newPoints);
      return;
    }

    // Always show hover highlighting regardless of tool, unless actively interacting
    if (!isDrawing && !dragging.active && !resizing.active && !marquee && !rotating.active && !polylinePoints && !curvePoints) {
      const target = findTargetAt(x, y);
      setHoveredId(target ? target.id : null);
    }

    if (rotating.active && selectedIds.length > 0) {
      const groupBB = getGroupBoundingBox(selectedIds);
      const currentAngle = Math.atan2(y - groupBB.cy, x - groupBB.cx) * (180 / Math.PI);
      const delta = currentAngle - rotating.startAngle;
      setElements(prev => prev.map(el => selectedIds.includes(el.id) ? { ...el, rotate: (el.rotate || 0) + delta } : el));
      setRotating(prev => ({ ...prev, startAngle: currentAngle }));
      return;
    }

    if (marquee) {
      setMarquee(prev => ({
        ...prev,
        x: Math.min(x, prev.startX),
        y: Math.min(y, prev.startY),
        width: Math.abs(x - prev.startX),
        height: Math.abs(y - prev.startY)
      }));
      return;
    }

    if (dragging.active) {
      const dx = x - dragging.startX;
      const dy = y - dragging.startY;
      const { originalElements } = dragging;

      // Snapping during drag
      let totalDx = dx;
      let totalDy = dy;

      if (selectedIds.length === 1 && !e.altKey) {
        const el = originalElements && originalElements[0];
        if (el && el.subtype) {
          const terminals = getTerminals(el);
          for (const t of terminals) {
            const snap = getSnapPoint(t.x + dx, t.y + dy, selectedIds);
            if (snap) {
              totalDx = dx + (snap.x - (t.x + dx));
              totalDy = dy + (snap.y - (t.y + dy));
              break;
            }
          }
        }
      }

      setElements(prev => prev.map(el => {
        if (!selectedIds.includes(el.id)) return el;
        const orig = (originalElements || []).find(o => o.id === el.id);
        if (!orig) return el;

        if (el.type === 'rect') return { ...el, x: orig.x + totalDx, y: orig.y + totalDy };
        if (el.type === 'circle') return { ...el, cx: orig.cx + totalDx, cy: orig.cy + totalDy };
        if (el.type === 'line') return { ...el, x1: orig.x1 + totalDx, y1: orig.y1 + totalDy, x2: orig.x2 + totalDx, y2: orig.y2 + totalDy };
        if (el.type === 'text') return { ...el, x: orig.x + totalDx, y: orig.y + totalDy };
        if (el.type === 'path') {
          const newD = orig.d.replace(/([MLHVCSQTAZmlhvcsqtaz])([^MLHVCSQTAZmlhvcsqtaz]*)/g, (match, cmd, args) => {
            const coords = Array.from(args.matchAll(/[-+]?(?:\d*\.\d+|\d+\.?)(?:[eE][-+]?\d+)?/g)).map(m => parseFloat(m[0]));
            if (coords.length === 0 && cmd.toLowerCase() !== 'z') return match;
            const lowerCmd = cmd.toLowerCase();
            const isAbsolute = cmd === cmd.toUpperCase() && cmd !== 'z';
            let transformedArgs = '';

            const argCounts = { m: 2, l: 2, h: 1, v: 1, c: 6, s: 4, q: 4, t: 2, a: 7, z: 0 };
            const argCount = argCounts[lowerCmd] || 2;

            if (argCount > 0) {
              for (let i = 0; i < coords.length; i += argCount) {
                const currentArgs = coords.slice(i, i + argCount);
                if (currentArgs.length < argCount && argCount > 0) break;

                if (!isAbsolute) {
                  transformedArgs += ` ${currentArgs.join(' ')}`;
                  continue;
                }

                if (lowerCmd === 'h') {
                  transformedArgs += ` ${currentArgs[0] + totalDx}`;
                } else if (lowerCmd === 'v') {
                  transformedArgs += ` ${currentArgs[0] + totalDy}`;
                } else if (lowerCmd === 'a') {
                  const rx = currentArgs[0], ry = currentArgs[1], rot = currentArgs[2], laf = currentArgs[3], sf = currentArgs[4];
                  transformedArgs += ` ${rx} ${ry} ${rot} ${laf} ${sf} ${currentArgs[5] + totalDx} ${currentArgs[6] + totalDy}`;
                } else {
                  for (let j = 0; j < currentArgs.length; j += 2) {
                    if (j + 1 < currentArgs.length) {
                      const px = (currentArgs[j] || 0) + totalDx;
                      const py = (currentArgs[j + 1] || 0) + totalDy;
                      transformedArgs += ` ${px} ${py}`;
                    }
                  }
                }
              }
            }
            return cmd + transformedArgs;
          });
          const newPoints = orig.points ? orig.points.map(p => ({ x: p.x + totalDx, y: p.y + totalDy })) : null;
          let updated = { ...el, d: newD, points: newPoints, minX: orig.minX + totalDx, maxX: orig.maxX + totalDx, minY: orig.minY + totalDy, maxY: orig.maxY + totalDy };
          if (el.isAngle) {
            updated.originX = (orig.originX || 0) + totalDx;
            updated.originY = (orig.originY || 0) + totalDy;
          }
          return updated;
        }
        return el;
      }));
      return;
    }

    if (resizing.active) {
      const { handle, originalBB, originalElements } = resizing;

      if (handle.startsWith('vertex-')) {
        const index = parseInt(handle.split('-')[1]);
        const snap = getSnapPoint(x, y, selectedIds);
        const px = snap ? snap.x : x;
        const py = snap ? snap.y : y;

        setElements(prev => prev.map(el => {
          if (!selectedIds.includes(el.id)) return el;
          const newPoints = [...el.points];
          newPoints[index] = { x: px, y: py };

          let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
          newPoints.forEach(p => {
            minX = Math.min(minX, p.x); minY = Math.min(minY, p.y);
            maxX = Math.max(maxX, p.x); maxY = Math.max(maxY, p.y);
          });

          let d;
          if (el.subtype === 'curve') {
            // Regenerate curve path using quadratic Bezier
            d = `M ${newPoints[0].x} ${newPoints[0].y}`;
            if (newPoints.length === 2) {
              d += ` L ${newPoints[1].x} ${newPoints[1].y}`;
            } else {
              for (let i = 1; i < newPoints.length; i++) {
                const controlX = newPoints[i].x;
                const controlY = newPoints[i].y;
                if (i < newPoints.length - 1) {
                  const nextX = newPoints[i + 1].x;
                  const nextY = newPoints[i + 1].y;
                  d += ` Q ${controlX} ${controlY} ${nextX} ${nextY}`;
                  i++;
                }
              }
            }
          } else {
            // Polyline: use straight line segments
            d = `M ${newPoints[0].x} ${newPoints[0].y} ` + newPoints.slice(1).map(p => `L ${p.x} ${p.y}`).join(' ');
          }
          return { ...el, points: newPoints, d, minX, minY, maxX, maxY };
        }));
        return;
      }

      let newBB = { ...originalBB };

      // Support resizing rotated elements by rotating the mouse coordinate back to the element's local frame
      const singleEl = selectedIds.length === 1 ? elements.find(e => e.id === selectedIds[0]) : null;
      const rotateAngle = singleEl ? (singleEl.rotate || 0) : 0;
      const local = rotatePoint(x, y, originalBB.cx, originalBB.cy, -rotateAngle);
      const localX = local.x;
      const localY = local.y;

      if (e.ctrlKey) {
        // Resize from Center
        const halfWidth = Math.max(5, Math.abs(localX - originalBB.cx));
        const halfHeight = Math.max(5, Math.abs(localY - originalBB.cy));

        if (handle.includes('l') || handle.includes('r')) {
          newBB.width = halfWidth * 2;
          newBB.x = originalBB.cx - halfWidth;
        }
        if (handle.includes('t') || handle.includes('b')) {
          newBB.height = halfHeight * 2;
          newBB.y = originalBB.cy - halfHeight;
        }
      } else {
        if (handle.includes('r')) newBB.width = Math.max(5, localX - originalBB.x);
        if (handle.includes('b')) newBB.height = Math.max(5, localY - originalBB.y);
        if (handle.includes('l')) { newBB.x = localX; newBB.width = Math.max(5, originalBB.x + originalBB.width - localX); }
        if (handle.includes('t')) { newBB.y = localY; newBB.height = Math.max(5, originalBB.y + originalBB.height - localY); }
      }

      const scaleX = originalBB.width === 0 ? 1 : newBB.width / originalBB.width;
      const scaleY = originalBB.height === 0 ? 1 : newBB.height / originalBB.height;

      setElements(prev => prev.map(el => {
        if (!selectedIds.includes(el.id)) return el;
        const orig = originalElements.find(o => o.id === el.id);

        // Use uniform scaling unless Ctrl is held (Free Resize)
        const sX = (el.subtype && !e.ctrlKey) ? Math.min(scaleX, scaleY) : scaleX;
        const sY = (el.subtype && !e.ctrlKey) ? Math.min(scaleX, scaleY) : scaleY;

        const transformX = (px) => (px - originalBB.x) * sX + newBB.x;
        const transformY = (py) => (py - originalBB.y) * sY + newBB.y;

        if (el.type === 'rect') return { ...el, x: transformX(orig.x), y: transformY(orig.y), width: orig.width * sX, height: orig.height * sY };
        if (el.type === 'circle') return { ...el, cx: transformX(orig.cx), cy: transformY(orig.cy), r: Math.max(1, orig.r * Math.sqrt(Math.abs(sX * sY))) };
        if (el.type === 'line') return { ...el, x1: transformX(orig.x1), y1: transformY(orig.y1), x2: transformX(orig.x2), y2: transformY(orig.y2) };
        if (el.type === 'path') {
          // Robust path resizing: Transform absolute points, scale relative deltas.
          const newD = orig.d.replace(/([MLHVCSQTAZmlhvcsqtaz])([^MLHVCSQTAZmlhvcsqtaz]*)/g, (match, cmd, args) => {
            const coords = Array.from(args.matchAll(/[-+]?(?:\d*\.\d+|\d+\.?)(?:[eE][-+]?\d+)?/g)).map(m => parseFloat(m[0]));
            if (coords.length === 0 && cmd.toLowerCase() !== 'z') return match;

            let transformedArgs = '';
            const lowerCmd = cmd.toLowerCase();

            if (lowerCmd === 'a') {
              for (let i = 0; i < coords.length; i += 7) {
                const rx = parseFloat(coords[i]) * Math.abs(sX);
                const ry = parseFloat(coords[i + 1]) * Math.abs(sY);
                const rot = coords[i + 2];
                const laf = coords[i + 3];
                const sf = (sX * sY < 0) ? (Number(coords[i + 4]) === 1 ? 0 : 1) : coords[i + 4];
                const endX = parseFloat(coords[i + 5]);
                const endY = parseFloat(coords[i + 6]);
                
                if (cmd === 'A') {
                  transformedArgs += ` ${rx} ${ry} ${rot} ${laf} ${sf} ${transformX(endX)} ${transformY(endY)}`;
                } else {
                  transformedArgs += ` ${rx} ${ry} ${rot} ${laf} ${sf} ${endX * sX} ${endY * sY}`;
                }
              }
            } else {
              const argCounts = { m: 2, l: 2, h: 1, v: 1, c: 6, s: 4, q: 4, t: 2, z: 0 };
              const argCount = argCounts[lowerCmd] || 2;
              const isAbsolute = cmd === cmd.toUpperCase();

              if (argCount > 0) {
                for (let i = 0; i < coords.length; i += argCount) {
                const currentArgs = coords.slice(i, i + argCount);
                if (currentArgs.length < argCount && argCount > 0) break;

                if (!isAbsolute) {
                  for (let j = 0; j < currentArgs.length; j += 2) {
                    if (j + 1 < currentArgs.length) {
                      transformedArgs += ` ${parseFloat(currentArgs[j]) * sX} ${parseFloat(currentArgs[j + 1]) * sY}`;
                    } else if (currentArgs.length === 1) {
                      transformedArgs += ` ${parseFloat(currentArgs[j]) * (lowerCmd === 'h' ? sX : sY)}`;
                    }
                  }
                  continue;
                }

                if (lowerCmd === 'h') {
                  transformedArgs += ` ${transformX(parseFloat(currentArgs[0]))}`;
                } else if (lowerCmd === 'v') {
                  transformedArgs += ` ${transformY(parseFloat(currentArgs[0]))}`;
                } else {
                  for (let j = 0; j < currentArgs.length; j += 2) {
                    if (j + 1 < currentArgs.length) {
                      transformedArgs += ` ${transformX(parseFloat(currentArgs[j]))} ${transformY(parseFloat(currentArgs[j + 1]))}`;
                    }
                  }
                }
              }
            }
          }
          return cmd + transformedArgs;
          });

          const newMinX = Math.min(transformX(orig.minX), transformX(orig.maxX));
          const newMaxX = Math.max(transformX(orig.minX), transformX(orig.maxX));
          const newMinY = Math.min(transformY(orig.minY), transformY(orig.maxY));
          const newMaxY = Math.max(transformY(orig.minY), transformY(orig.maxY));
          
          let updated = { ...el, d: newD, minX: newMinX, maxX: newMaxX, minY: newMinY, maxY: newMaxY };
          if (el.isAngle) {
            updated.originX = transformX(orig.originX || 0);
            updated.originY = transformY(orig.originY || 0);
          }
          return updated;
        }
        return el;
      }));
      return;
    }

    if (!isDrawing || !tempElement) return;

    const snap = getSnapPoint(x, y);
    const finalX = snap ? snap.x : x;
    const finalY = (snap && tool !== 'pencil') ? snap.y : y; // Avoid snapping pencil for smooth drawing

    if (tool === 'pencil') {
      setTempElement(prev => ({ ...prev, d: `${prev.d} L ${x} ${y}`, minX: Math.min(prev.minX, x), minY: Math.min(prev.minY, y), maxX: Math.max(prev.maxX, x), maxY: Math.max(prev.maxY, y) }));
    } else if (tool === 'rect') {
      setTempElement(prev => ({ ...prev, width: Math.abs(x - prev.x), height: Math.abs(y - prev.y), x: Math.min(x, prev.x), y: Math.min(y, prev.y) }));
    } else if (tool === 'circle') {
      setTempElement(prev => ({ ...prev, r: Math.sqrt(Math.pow(x - tempElement.cx, 2) + Math.pow(y - tempElement.cy, 2)) }));
    } else if (tool === 'line') {
      setTempElement(prev => ({ ...prev, x2: finalX, y2: finalY }));
    }
  };

  const endInteraction = (e) => {
    if (marquee) {
      const selected = elements.filter(el => {
        const bb = getBoundingBox(el);
        return bb && bb.x >= marquee.x && bb.y >= marquee.y && (bb.x + bb.width) <= (marquee.x + marquee.width) && (bb.y + bb.height) <= (marquee.y + marquee.height);
      }).map(el => el.id);

      if (e && e.ctrlKey) {
        setSelectedIds(prev => [...new Set([...prev, ...selected])]);
      } else {
        setSelectedIds(selected);
      }
      setMarquee(null);
    } else if (isDrawing && tempElement) {
      pushHistory([...elements, tempElement]);
      setTool('select');
    } else if (dragging.active || resizing.active || rotating.active) {
      pushHistory(elements);
    }
    setIsDrawing(false);
    setResizing({ active: false });
    setDragging({ active: false });
    setRotating({ active: false });
    setTempElement(null);
    setHoveredId(null);
  };

  const getHandles = (bb) => {
    const { x, y, width, height } = bb;

    // If only one polyline or curve is selected, add vertex handles and rotation handle
    if (selectedIds.length === 1) {
      const el = elements.find(e => e.id === selectedIds[0]);
      if (el && (el.subtype === 'polyline' || el.subtype === 'curve')) {
        return [
          ...el.points.map((p, i) => ({ x: p.x, y: p.y, type: `vertex-${i}` })),
          { x: x + width / 2, y: y - 30, type: 'rotate' }
        ];
      }
    }

    return [
      { x, y, type: 'tl' }, { x: x + width / 2, y, type: 't' }, { x: x + width, y, type: 'tr' },
      { x: x + width, y: y + height / 2, type: 'r' }, { x: x + width, y: y + height, type: 'br' },
      { x: x + width / 2, y: y + height, type: 'b' }, { x, y: y + height, type: 'bl' }, { x, y: y + height / 2, type: 'l' },
      { x: x + width / 2, y: y - 30, type: 'rotate' }
    ];
  };

  const renderElement = (el, isSelected, isHovered) => {
    const bb = getBoundingBox(el);
    const props = {
      stroke: el.stroke,
      strokeWidth: el.strokeWidth,
      fill: el.subtype ? "none" : (el.fill || "transparent"),
      strokeLinecap: "round",
      strokeLinejoin: "round",
      strokeDasharray: el.isDashed ? "8,8" : "none",
      style: { cursor: tool === 'select' ? 'pointer' : 'crosshair', transition: 'stroke 0.2s, stroke-width 0.2s, fill 0.2s' }
    };

    const highlight = isHovered ? (
      <g opacity="0.4">
        {(() => {
          const hProps = { ...props, stroke: "#4f46e5", strokeWidth: parseFloat(el.strokeWidth) + 8, fill: "none" };
          switch (el.type) {
            case 'path': return <path d={el.d} {...hProps} />;
            case 'rect': return <rect x={el.x} y={el.y} width={el.width} height={el.height} {...hProps} />;
            case 'circle': return <circle cx={el.cx} cy={el.cy} r={el.r} {...hProps} />;
            case 'line': return <line x1={el.x1} y1={el.y1} x2={el.x2} y2={el.y2} {...hProps} />;
            case 'text': return (
              <text 
                x={el.x} 
                y={el.y} 
                textAnchor="middle" 
                dominantBaseline="central" 
                fontSize={el.fontSize || 18} 
                {...hProps} 
                fill="#4f46e5" 
                opacity="0.5" 
                style={{
                  fontFamily: 'KaTeX_Math, "Latin Modern Math", "Times New Roman", Cambria Math, serif',
                  fontStyle: 'italic',
                  fontWeight: el.fontWeight || 'normal'
                }}
              >
                {el.text}
              </text>
            );
            default: return null;
          }
        })()}
      </g>
    ) : null;

    const terminals = getTerminals(el);

    const element = (() => {
      if (el.subtype === 'npn' || el.subtype === 'pnp') {
        const isNPN = el.subtype === 'npn';
        const w = bb.width / 60;
        const h = bb.height / 40;
        return (
          <g>
            <path d={el.d} {...props} />
            {isNPN ? (
              <path d={`M ${bb.cx + 13.75*w} ${bb.cy + 17*h} L ${bb.cx + 2.5*w - 2.4*h} ${bb.cy + 11.6*h + 5.0*w} L ${bb.cx + 2.5*w + 2.4*h} ${bb.cy + 11.6*h - 5.0*w} Z`} fill={el.stroke} stroke="none" />
            ) : (
              <path d={`M ${bb.cx + 2.5*w} ${bb.cy + 11.6*h} L ${bb.cx + 13.75*w - 2.4*h} ${bb.cy + 17*h + 5.0*w} L ${bb.cx + 13.75*w + 2.4*h} ${bb.cy + 17*h - 5.0*w} Z`} fill={el.stroke} stroke="none" />
            )}
            {el.label && (
               <text x={bb.cx} y={bb.y - 15} textAnchor="middle" fontSize="14" fill={el.stroke} fontWeight="bold" style={{ userSelect: 'none' }}>{el.label}</text>
            )}
          </g>
        );
      }
      switch (el.type) {
        case 'path': return <path d={el.d} {...props} />;
        case 'rect': return <rect x={el.x} y={el.y} width={el.width} height={el.height} {...props} />;
        case 'circle': return <circle cx={el.cx} cy={el.cy} r={el.r} {...props} />;
        case 'line': return <line x1={el.x1} y1={el.y1} x2={el.x2} y2={el.y2} {...props} />;
        case 'text':
          return (
            <text
              x={el.x}
              y={el.y}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={el.fontSize || 18}
              fill={el.stroke || "currentColor"}
              style={{
                cursor: tool === 'select' ? 'pointer' : 'crosshair',
                userSelect: 'none',
                fontFamily: 'KaTeX_Math, "Latin Modern Math", "Times New Roman", Cambria Math, serif',
                fontStyle: 'italic',
                fontWeight: el.fontWeight || 'normal'
              }}
              onDoubleClick={(e) => {
                e.stopPropagation();
                setSelectedIds([el.id]);
                setTimeout(() => {
                  const input = document.querySelector('.keyboard-text-input');
                  if (input) {
                    input.focus();
                    input.select();
                  }
                }, 50);
              }}
            >
              {el.text}
            </text>
          );
        default: return null;
      }
    })();

    const transform = `translate(${bb.cx} ${bb.cy}) rotate(${el.rotate || 0}) scale(${el.flipX || 1} ${el.flipY || 1}) translate(${-bb.cx} ${-bb.cy})`;

    return (
      <g key={el.id}>
        <g transform={transform}>
          {highlight}
          {element}
          {el.label && (() => {
            let lx = bb.cx, ly = bb.cy;
            if (el.isAngle) {
              const rad = ((el.angleValue || 45) / 2) * (Math.PI / 180);
              const r = 40;
              lx = (el.originX || 0) + r * Math.cos(-rad);
              ly = (el.originY || 0) + r * Math.sin(-rad);
            }
            return (
              <text
                x={lx}
                y={ly}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize="14"
                fontWeight="bold"
                fill={el.stroke || "currentColor"}
                transform={`rotate(${-el.rotate || 0}, ${lx}, ${ly})`}
                style={{ pointerEvents: 'none', userSelect: 'none' }}
              >
                {el.label}
              </text>
            );
          })()}
        </g>
        {(isHovered || isSelected || tool === 'line' || isDrawing) && terminals.map((t, i) => (
          <circle key={`${el.id}-t-${i}`} cx={t.x} cy={t.y} r="3" fill="white" stroke={el.stroke} strokeWidth="1" pointerEvents="none" />
        ))}
      </g>
    );
  };

  // Floating LaTeX Math Symbol Keyboard
  const renderLatexKeyboard = () => {
    const activeTextEl = selectedIds.length === 1 ? elements.find(el => el.id === selectedIds[0]) : null;
    const isTextSelected = activeTextEl && activeTextEl.type === 'text';
    if (!isTextSelected) return null;

    const categories = [
      {
        title: "Greeks",
        keys: [
          { label: 'θ', code: '\\theta', title: 'theta' },
          { label: 'ω', code: '\\omega', title: 'omega' },
          { label: 'π', code: '\\pi', title: 'pi' },
          { label: 'μ', code: '\\mu', title: 'mu' },
          { label: 'Ω', code: '\\Omega', title: 'omega (cap)' },
          { label: 'Δ', code: '\\Delta', title: 'delta (cap)' },
          { label: 'α', code: '\\alpha', title: 'alpha' },
          { label: 'β', code: '\\beta', title: 'beta' },
          { label: 'λ', code: '\\lambda', title: 'lambda' },
          { label: 'φ', code: '\\phi', title: 'phi' }
        ]
      },
      {
        title: "Operators",
        keys: [
          { label: '√', code: '\\sqrt', title: 'square root' },
          { label: '∫', code: '\\int', title: 'integral' },
          { label: '∑', code: '\\sum', title: 'sum' },
          { label: '×', code: '\\times', title: 'multiply' },
          { label: '±', code: '\\pm', title: 'plus-minus' },
          { label: '∞', code: '\\infty', title: 'infinity' },
          { label: '°', code: '\\deg', title: 'degree' },
          { label: '∂', code: '\\partial', title: 'partial' },
          { label: '∠', code: '\\angle', title: 'angle' }
        ]
      },
      {
        title: "Scripts",
        keys: [
          { label: 'x²', code: '^2', title: 'squared' },
          { label: 'x³', code: '^3', title: 'cubed' },
          { label: 'x₀', code: '_0', title: 'subscript 0' },
          { label: 'x₁', code: '_1', title: 'subscript 1' },
          { label: 'xₙ', code: '_n', title: 'subscript n' },
          { label: 'xᵗ', code: '^t', title: 'superscript t' }
        ]
      }
    ];

    return (
      <div 
        className="latex-math-keyboard"
        style={{
          position: 'absolute',
          bottom: '16px',
          right: '16px',
          transform: `translate(${keyboardOffset.x}px, ${keyboardOffset.y}px)`,
          background: 'rgba(15, 23, 42, 0.95)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '12px',
          padding: '12px',
          width: '280px',
          maxHeight: 'calc(100% - 48px)',
          boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.5), inset 0 1px 0 0 rgba(255, 255, 255, 0.05)',
          zIndex: 100,
          userSelect: 'none',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          fontFamily: "'Outfit', sans-serif"
        }}
        onMouseDown={(e) => e.stopPropagation()} // Prevent deselecting or canvas interactions
      >
        <div 
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            borderBottom: '1px solid rgba(255,255,255,0.06)', 
            paddingBottom: '8px',
            cursor: 'move'
          }}
          onMouseDown={handleKeyboardDragStart}
        >
          <span style={{ fontSize: '0.76rem', fontWeight: 700, color: 'var(--primary)', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
            <i className="fas fa-keyboard" style={{ marginRight: '6px' }}></i> Math LaTeX Keyboard
          </span>
          <span style={{ fontSize: '0.62rem', color: '#64748b' }}>Click to insert at cursor</span>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', margin: '4px 0' }}>
          <span style={{ fontSize: '0.65rem', fontWeight: 600, color: '#94a3b8' }}>Edit Formula:</span>
          <input
            type="text"
            className="num-input keyboard-text-input"
            style={{
              width: '100%',
              height: '36px',
              padding: '0 10px',
              fontSize: '0.88rem',
              fontFamily: 'KaTeX_Math, "Latin Modern Math", "Times New Roman", Cambria Math, serif',
              fontStyle: 'italic',
              background: '#1e293b',
              border: '1px solid var(--primary)',
              borderRadius: '6px',
              color: '#f8fafc',
              boxShadow: '0 0 8px var(--primary-alpha)',
              outline: 'none'
            }}
            value={activeTextEl.text || ''}
            onChange={(e) => handleTextChange(e.target.value)}
            placeholder="LaTeX e.g. \theta"
            title="Edit mathematical text"
            onFocus={() => setIsKeyboardInputFocused(true)}
            onBlur={() => setIsKeyboardInputFocused(false)}
          />
          
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '4px' }}>
            {/* Font Size Adjuster */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '0.65rem', fontWeight: 600, color: '#64748b' }}>Size:</span>
              <div style={{ display: 'flex', alignItems: 'center', background: '#1e293b', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '6px', overflow: 'hidden' }}>
                <button 
                  style={{ width: '22px', height: '22px', background: 'transparent', border: 'none', color: '#cbd5e1', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem' }}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    const size = Math.max(12, (activeTextEl.fontSize || 18) - 2);
                    updateSelectedElements({ fontSize: size });
                  }}
                  title="Decrease font size"
                >-</button>
                <span style={{ fontSize: '0.68rem', color: '#f8fafc', width: '32px', textAlign: 'center', fontFamily: 'monospace' }}>
                  {activeTextEl.fontSize || 18}px
                </span>
                <button 
                  style={{ width: '22px', height: '22px', background: 'transparent', border: 'none', color: '#cbd5e1', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem' }}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    const size = Math.min(72, (activeTextEl.fontSize || 18) + 2);
                    updateSelectedElements({ fontSize: size });
                  }}
                  title="Increase font size"
                >+</button>
              </div>
            </div>

            {/* Thickness / Weight Toggle */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '0.65rem', fontWeight: 600, color: '#64748b' }}>Thickness:</span>
              <button
                style={{
                  height: '22px',
                  padding: '0 8px',
                  background: activeTextEl.fontWeight === 'bold' ? 'var(--primary-alpha)' : '#1e293b',
                  border: activeTextEl.fontWeight === 'bold' ? '1px solid var(--primary)' : '1px solid rgba(255,255,255,0.08)',
                  borderRadius: '6px',
                  color: activeTextEl.fontWeight === 'bold' ? 'var(--primary-light)' : '#cbd5e1',
                  fontSize: '0.68rem',
                  fontWeight: activeTextEl.fontWeight === 'bold' ? 'bold' : 'normal',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease'
                }}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  const nextWeight = activeTextEl.fontWeight === 'bold' ? 'normal' : 'bold';
                  updateSelectedElements({ fontWeight: nextWeight });
                }}
                title="Toggle text thickness (Normal / Bold)"
              >
                {activeTextEl.fontWeight === 'bold' ? 'Bold ✦' : 'Normal ✧'}
              </button>
            </div>
          </div>
        </div>

        <div 
          className="math-keyboard-keys-container"
          style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '8px',
            overflowY: 'auto',
            flex: 1,
            minHeight: 0,
            paddingRight: '4px'
          }}
        >
          {categories.map(cat => (
            <div key={cat.title} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <span style={{ fontSize: '0.62rem', fontWeight: 600, color: '#475569', letterSpacing: '0.5px' }}>{cat.title}</span>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '6px' }}>
                {cat.keys.map(key => (
                  <button
                    key={key.code}
                    className="math-key-btn"
                    title={`${key.title} (${key.code})`}
                    style={{
                      height: '32px',
                      background: 'rgba(255, 255, 255, 0.04)',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                      borderRadius: '6px',
                      color: '#f8fafc',
                      fontSize: '0.85rem',
                      fontFamily: 'KaTeX_Math, "Latin Modern Math", "Times New Roman", Cambria Math, serif',
                      fontStyle: 'italic',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.15s ease'
                    }}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => insertSymbol(key.code)}
                  >
                    {key.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Top Edit Menu */}
      <div className="svg-editor-top-menu" style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        padding: '10px 16px',
        background: '#0f172a',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        color: '#cbd5e1',
        zIndex: 10,
        fontFamily: "'Outfit', sans-serif",
        flexShrink: 0
      }}>
        {/* Left / Center Controls */}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '16px' }}>
          {/* Sidebar Toggle */}
          <button 
            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)} 
            className="tool-button icon-btn" 
            title={isSidebarCollapsed ? "Show Elements Sidebar" : "Hide Elements Sidebar"} 
            style={{ width: '32px', height: '32px', padding: 0, justifyContent: 'center' }}
          >
            <i className={`fas ${isSidebarCollapsed ? 'fa-chevron-right' : 'fa-chevron-left'}`} style={{ fontSize: '0.8rem' }}></i>
          </button>

          <div style={{ width: '1px', height: '20px', background: 'rgba(255,255,255,0.1)' }} />

          {/* Edit/History */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <button onClick={undo} className="tool-button icon-btn" disabled={historyIndex === 0} title="Undo (Ctrl+Z)" style={{ width: '32px', height: '32px', padding: 0 }}>
              <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" fill="none" strokeWidth="2"><path d="M3 10h10a5 5 0 0 1 0 10H11 M3 10l4-4 M3 10l4 4" /></svg>
            </button>
            <button onClick={redo} className="tool-button icon-btn" disabled={historyIndex === history.length - 1} title="Redo (Ctrl+Y)" style={{ width: '32px', height: '32px', padding: 0 }}>
              <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" fill="none" strokeWidth="2"><path d="M21 10H11a5 5 0 0 0 0 10h2 M21 10l-4-4 M21 10l4 4" /></svg>
            </button>
            <button onClick={deleteSelected} className="tool-button icon-btn danger" disabled={selectedIds.length === 0} title="Delete Selected (Delete)" style={{ width: '32px', height: '32px', padding: 0 }}>
              <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" fill="none" strokeWidth="2"><path d="M3 6h18 M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2 M10 11v6 M14 11v6" /></svg>
            </button>
            <button onClick={() => { if (window.confirm("Are you sure you want to clear the canvas?")) pushHistory([]); }} className="tool-button icon-btn danger" title="Clear All Canvas" style={{ width: '32px', height: '32px', padding: 0 }}>
              <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" fill="none" strokeWidth="2"><path d="M19 6L5 20 M5 6l14 14" /></svg>
            </button>
          </div>

          <div style={{ width: '1px', height: '20px', background: 'rgba(255,255,255,0.1)' }} />

          {/* Arrange / Transform */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <button onClick={() => {
              const newElements = elements.map(el => selectedIds.includes(el.id) ? { ...el, flipX: (el.flipX || 1) * -1 } : el);
              setElements(newElements);
              pushHistory(newElements);
            }} className="tool-button icon-btn" disabled={selectedIds.length === 0} title="Flip Horizontal (H)" style={{ width: '32px', height: '32px', padding: 0 }}>
              <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" fill="none" strokeWidth="2"><path d="M4 12h16 M12 4l-4 4 4 4 M12 20l-4-4 4-4" transform="rotate(90 12 12)" /><line x1="12" y1="2" x2="12" y2="22" strokeDasharray="4" /></svg>
            </button>
            <button onClick={() => {
              const newElements = elements.map(el => selectedIds.includes(el.id) ? { ...el, flipY: (el.flipY || 1) * -1 } : el);
              setElements(newElements);
              pushHistory(newElements);
            }} className="tool-button icon-btn" disabled={selectedIds.length === 0} title="Flip Vertical (V)" style={{ width: '32px', height: '32px', padding: 0 }}>
              <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" fill="none" strokeWidth="2"><path d="M4 12h16 M12 4l-4 4 4 4 M12 20l-4-4 4-4" /><line x1="2" y1="12" x2="22" y2="12" strokeDasharray="4" /></svg>
            </button>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: '4px' }}>
              <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#94a3b8' }}>Rotate:</span>
              <input
                type="number"
                value={selectedIds.length === 1 ? Math.round(elements.find(el => el.id === selectedIds[0])?.rotate || 0) : ''}
                onChange={(e) => {
                  const val = parseInt(e.target.value) || 0;
                  const newElements = elements.map(el => selectedIds.includes(el.id) ? { ...el, rotate: val } : el);
                  setElements(newElements);
                  pushHistory(newElements);
                }}
                disabled={selectedIds.length === 0}
                className="num-input"
                style={{ width: '50px', height: '28px', padding: '0 4px', fontSize: '0.75rem' }}
                placeholder={selectedIds.length > 1 ? 'Mix' : '0'}
              />
              <span style={{ fontSize: '0.75rem', color: '#64748b' }}>°</span>
            </div>


          </div>

          <div style={{ width: '1px', height: '20px', background: 'rgba(255,255,255,0.1)' }} />

          {/* Appearance */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#94a3b8' }}>Stroke:</span>
            <input type="color" value={color === 'currentColor' ? '#000000' : color} onChange={(e) => handleColorChange(e.target.value)} className="color-picker" style={{ width: '28px', height: '28px' }} title="Stroke Color" />
            <input type="number" value={penWidth} onChange={(e) => handlePenWidthChange(parseInt(e.target.value))} min="1" max="20" className="num-input" style={{ width: '40px', height: '28px', padding: '0 4px' }} title="Pen Width" />
            
            <label className="check-group" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '0.75rem' }} title="Enable/Disable Fill">
              <input type="checkbox" checked={showFill} onChange={(e) => handleShowFillChange(e.target.checked)} style={{ width: '14px', height: '14px', margin: 0 }} />
              Fill
            </label>
            {showFill && <input type="color" value={fillColor === 'transparent' || fillColor === 'currentColor' ? '#000000' : fillColor} onChange={(e) => handleFillColorChange(e.target.value)} className="color-picker" style={{ width: '28px', height: '28px' }} title="Fill Color" />}
          </div>

          <div style={{ width: '1px', height: '20px', background: 'rgba(255,255,255,0.1)' }} />

          {/* Environment */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label className="check-group" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '0.75rem' }} title="Toggle Dashed Lines">
              <input type="checkbox" checked={isDashed} onChange={(e) => handleIsDashedChange(e.target.checked)} style={{ width: '14px', height: '14px', margin: 0 }} />
              Dash
            </label>
            <label className="check-group" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '0.75rem' }} title="Toggle Grid Visibility">
              <input type="checkbox" checked={showGrid} onChange={(e) => setShowGrid(e.target.checked)} style={{ width: '14px', height: '14px', margin: 0 }} />
              Grid
            </label>
          </div>
        </div>

        {/* Far Right: Save & Exit */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {onCancel && (
            <button 
              onClick={onCancel} 
              className="tool-button danger" 
              style={{ height: '32px', padding: '0 12px', fontSize: '0.78rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              title="Exit without saving"
            >
              <i className="fas fa-arrow-left" style={{ marginRight: '6px' }}></i> Exit
            </button>
          )}
          <button 
            onClick={handleSaveCropped} 
            className="tool-button primary" 
            style={{ height: '32px', padding: '0 12px', fontSize: '0.78rem', marginTop: 0 }}
          >
            Insert
          </button>
        </div>
      </div>

      <div className="svg-editor-container" style={{ borderRadius: 0, boxShadow: 'none', border: 'none', flex: 1, minHeight: 0, position: 'relative' }}>
        <div 
          className="svg-editor-toolbar" 
          style={isSidebarCollapsed ? {
            width: 0,
            minWidth: 0,
            padding: 0,
            borderRight: 'none',
            overflow: 'hidden'
          } : {}}
        >
        <div className="tool-label">Basic Tools</div>
        <div className="toolbar-row">
          <button onClick={() => setTool('pencil')} className={`tool-button icon-btn ${tool === 'pencil' ? 'active' : ''}`} title="Pencil Tool (P)">
            <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" fill="none" strokeWidth="2"><path d="M12 19l7-7 3 3-7 7-3-3z M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z M2 2l5 5" /></svg>
          </button>
          <button onClick={() => setTool('rect')} className={`tool-button icon-btn ${tool === 'rect' ? 'active' : ''}`} title="Rectangle Tool (R)">
            <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" fill="none" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /></svg>
          </button>
          <button onClick={() => setTool('circle')} className={`tool-button icon-btn ${tool === 'circle' ? 'active' : ''}`} title="Circle Tool (C)">
            <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" fill="none" strokeWidth="2"><circle cx="12" cy="12" r="9" /></svg>
          </button>
          <button onClick={() => setTool('line')} className={`tool-button icon-btn ${tool === 'line' ? 'active' : ''}`} title="Line Tool (L)">
            <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" fill="none" strokeWidth="2"><line x1="5" y1="19" x2="19" y2="5" /></svg>
          </button>
          <button onClick={() => setTool('curve')} className={`tool-button icon-btn ${tool === 'curve' ? 'active' : ''}`} title="Bezier Curve Tool (B)">
            <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" fill="none" strokeWidth="2"><path d="M3 5 Q 12 3 21 8" /><circle cx="3" cy="5" r="2" fill="currentColor" /><circle cx="21" cy="8" r="2" fill="currentColor" /></svg>
          </button>
          <button onClick={() => setTool('select')} className={`tool-button icon-btn ${tool === 'select' ? 'active' : ''}`} title="Select & Move (V)">
            <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" fill="none" strokeWidth="2"><path d="M5 3l3.057 14.943L11.5 13l5.5 5.5 3-3-5.5-5.5 4.943-3.443L5 3z" /></svg>
          </button>
          <button onClick={() => setTool('text')} className={`tool-button icon-btn ${tool === 'text' ? 'active' : ''}`} title="Text Tool (T)">
            <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" fill="none" strokeWidth="2"><path d="M4 7V4h16v3M12 4v16m-4 0h8" /></svg>
          </button>
        </div>

        <div className="tool-divider" />
        <button onClick={() => setIsCircuitExpanded(!isCircuitExpanded)} className="section-btn">
          <span>Circuit Elements</span>
          <span>{isCircuitExpanded ? '▼' : '▶'}</span>
        </button>

        {isCircuitExpanded && (
          <div style={{ marginTop: '0.4rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
            <div>
              <div className="tool-label">Passive & Power</div>
              <div className="toolbar-row">
                <button onClick={() => setTool('resistor')} className={`tool-button icon-btn ${tool === 'resistor' ? 'active' : ''}`} title="Resistor">
                  <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" fill="none" strokeWidth="2"><path d="M2 12h3l1-3 2 6 2-6 2 6 2-6 2 6 1-3h3" /></svg>
                </button>
                <button onClick={() => setTool('potentiometer')} className={`tool-button icon-btn ${tool === 'potentiometer' ? 'active' : ''}`} title="Potentiometer">
                  <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" fill="none" strokeWidth="2"><path d="M2 12h3l1-3 2 6 2-6 2 6 2-6 2 6 1-3h3 M7 17l10-10 M14 7h3v3" /></svg>
                </button>
                <button onClick={() => setTool('capacitor')} className={`tool-button icon-btn ${tool === 'capacitor' ? 'active' : ''}`} title="Capacitor">
                  <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" fill="none" strokeWidth="2"><path d="M2 12h8 M14 12h8 M10 5v14 M14 5v14" /></svg>
                </button>
                <button onClick={() => setTool('vcap')} className={`tool-button icon-btn ${tool === 'vcap' ? 'active' : ''}`} title="Variable Capacitor">
                  <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" fill="none" strokeWidth="2"><path d="M2 12h8 M14 12h8 M10 5v14 M14 5v14 M7 17l10-10 M14 7h3v3" /></svg>
                </button>
                <button onClick={() => setTool('inductor')} className={`tool-button icon-btn ${tool === 'inductor' ? 'active' : ''}`} title="Inductor">
                  <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" fill="none" strokeWidth="2"><path d="M2 12 h2 a3 4 0 1 1 5 0 a3 4 0 1 1 5 0 a3 4 0 1 1 5 0 a3 4 0 1 1 5 0 h2" /></svg>
                </button>
                <button onClick={() => setTool('transformer')} className={`tool-button icon-btn ${tool === 'transformer' ? 'active' : ''}`} title="Transformer">
                  <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" fill="none" strokeWidth="2"><path d="M4 4v2 a3 3 0 0 1 0 4 a3 3 0 0 1 0 4 a3 3 0 0 1 0 4 v2 M20 4v2 a3 3 0 0 0 0 4 a3 3 0 0 0 0 4 a3 3 0 0 0 0 4 v2 M10 6v12 M14 6v12" /></svg>
                </button>
                <button onClick={() => setTool('switch')} className={`tool-button icon-btn ${tool === 'switch' ? 'active' : ''}`} title="Switch">
                  <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" fill="none" strokeWidth="2"><path d="M2 12h6 l8 -6 M16 12h6" /></svg>
                </button>
                <button onClick={() => setTool('fuse')} className={`tool-button icon-btn ${tool === 'fuse' ? 'active' : ''}`} title="Fuse">
                  <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" fill="none" strokeWidth="2"><path d="M2 12h7 M15 12h7 M9 8h6v8h-6z" /></svg>
                </button>
                <button onClick={() => setTool('connector')} className={`tool-button icon-btn ${tool === 'connector' ? 'active' : ''}`} title="Junction/Connector">
                  <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" fill="none" strokeWidth="2"><circle cx="12" cy="12" r="3" fill="currentColor" /></svg>
                </button>
                <button onClick={() => setTool('battery')} className={`tool-button icon-btn ${tool === 'battery' ? 'active' : ''}`} title="Battery">
                  <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" fill="none" strokeWidth="2"><path d="M2 12h7 M15 12h7 M9 5v14 M12 8v8 M15 5v14" /></svg>
                </button>
                <button onClick={() => setTool('ground')} className={`tool-button icon-btn ${tool === 'ground' ? 'active' : ''}`} title="Ground">
                  <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" fill="none" strokeWidth="2"><path d="M12 2v12 M6 14h12 M9 17h6 M11 20h2" /></svg>
                </button>
                <button onClick={() => setTool('dcsource')} className={`tool-button icon-btn ${tool === 'dcsource' ? 'active' : ''}`} title="DC Source">
                  <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" fill="none" strokeWidth="2"><circle cx="12" cy="12" r="9" /><path d="M10 12h4 M12 10v4" /></svg>
                </button>
                <button onClick={() => setTool('currentsource')} className={`tool-button icon-btn ${tool === 'currentsource' ? 'active' : ''}`} title="Current Source">
                  <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" fill="none" strokeWidth="2"><circle cx="12" cy="12" r="9" /><path d="M12 7v10 M10 10l2 -3 l2 3" /></svg>
                </button>
              </div>
            </div>

            <div>
              <div className="tool-label">Active & Semiconductors</div>
              <div className="toolbar-row">
                <button onClick={() => setTool('npn')} className={`tool-button icon-btn ${tool === 'npn' ? 'active' : ''}`} title="NPN Transistor">
                  <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" fill="none" strokeWidth="2"><circle cx="12" cy="12" r="9" /><path d="M4 12h4 M8 8v8 M8 10l4-2v-3 M8 14l4 2v3 M10 15l2 1l-1 -2Z" /></svg>
                </button>
                <button onClick={() => setTool('pnp')} className={`tool-button icon-btn ${tool === 'pnp' ? 'active' : ''}`} title="PNP Transistor">
                  <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" fill="none" strokeWidth="2"><circle cx="12" cy="12" r="9" /><path d="M4 12h4 M8 8v8 M8 10l4-2v-3 M8 14l4 2v3 M9 13.5l-2 -1l1.5 3Z" /></svg>
                </button>
                <button onClick={() => setTool('mosfet')} className={`tool-button icon-btn ${tool === 'mosfet' ? 'active' : ''}`} title="MOSFET">
                  <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" fill="none" strokeWidth="2"><path d="M7 8v8 M10 6v12 M10 9h8 M10 15h8 M18 6v3 M18 15v3" /></svg>
                </button>
                <button onClick={() => setTool('diode')} className={`tool-button icon-btn ${tool === 'diode' ? 'active' : ''}`} title="Diode">
                  <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" fill="none" strokeWidth="2"><path d="M2 12h6 M16 12h6 M8 6v12l8-6-8-6 M16 6v12" /></svg>
                </button>
                <button onClick={() => setTool('zener')} className={`tool-button icon-btn ${tool === 'zener' ? 'active' : ''}`} title="Zener Diode">
                  <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" fill="none" strokeWidth="2"><path d="M2 12h6 M16 12h6 M8 6v12l8-6-8-6 M16 6v12 M16 6l-3 0 M16 18l3 0" /></svg>
                </button>
                <button onClick={() => setTool('led')} className={`tool-button icon-btn ${tool === 'led' ? 'active' : ''}`} title="LED">
                  <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" fill="none" strokeWidth="2"><path d="M2 12h6 M16 12h6 M8 6v12l8-6-8-6 M16 6v12 M12 4l2-2 M16 7l2-2" /></svg>
                </button>
                <button onClick={() => setTool('opamp')} className={`tool-button icon-btn ${tool === 'opamp' ? 'active' : ''}`} title="Op-Amp">
                  <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" fill="none" strokeWidth="2"><path d="M4 6v12l12-6Z M2 9h2 M2 15h2 M16 12h4" /></svg>
                </button>
              </div>
            </div>

            <div>
              <div className="tool-label">Logic Gates</div>
              <div className="toolbar-row">
                <button onClick={() => setTool('and')} className={`tool-button icon-btn ${tool === 'and' ? 'active' : ''}`} title="AND Gate">
                  <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" fill="none" strokeWidth="2"><path d="M2 9h4 M2 15h4 M6 6v12h5 a6 6 0 0 0 0 -12 Z M17 12h5" /></svg>
                </button>
                <button onClick={() => setTool('or')} className={`tool-button icon-btn ${tool === 'or' ? 'active' : ''}`} title="OR Gate">
                  <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" fill="none" strokeWidth="2"><path d="M2 9h5.2 M2 15h5.2 M6 6 c2 2 2 10 0 12 c6 0 11-3 11-6 c0-3 -5-6 -11-6 Z M17 12h5" /></svg>
                </button>
                <button onClick={() => setTool('xor')} className={`tool-button icon-btn ${tool === 'xor' ? 'active' : ''}`} title="XOR Gate">
                  <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" fill="none" strokeWidth="2"><path d="M2 9h5.2 M2 15h5.2 M3.5 6 c2 2 2 10 0 12 M6 6 c2 2 2 10 0 12 c6 0 11-3 11-6 c0-3 -5-6 -11-6 Z M17 12h5" /></svg>
                </button>
                <button onClick={() => setTool('nand')} className={`tool-button icon-btn ${tool === 'nand' ? 'active' : ''}`} title="NAND Gate">
                  <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" fill="none" strokeWidth="2"><path d="M2 9h4 M2 15h4 M6 6v12h5 a6 6 0 0 0 0 -12 Z M20.5 12h3.5" /><circle cx="19" cy="12" r="1.5" /></svg>
                </button>
                <button onClick={() => setTool('nor')} className={`tool-button icon-btn ${tool === 'nor' ? 'active' : ''}`} title="NOR Gate">
                  <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" fill="none" strokeWidth="2"><path d="M2 9h5.2 M2 15h5.2 M6 6 c2 2 2 10 0 12 c6 0 11-3 11-6 c0-3 -5-6 -11-6 Z M20.5 12h3.5" /><circle cx="19" cy="12" r="1.5" /></svg>
                </button>
                <button onClick={() => setTool('not')} className={`tool-button icon-btn ${tool === 'not' ? 'active' : ''}`} title="NOT Gate">
                  <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" fill="none" strokeWidth="2"><path d="M2 12h4 M6 6v12l10-6 Z M19 12h5" /><circle cx="17.5" cy="12" r="1.5" /></svg>
                </button>
                <button onClick={() => setTool('xnor')} className={`tool-button icon-btn ${tool === 'xnor' ? 'active' : ''}`} title="XNOR Gate">
                  <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" fill="none" strokeWidth="2"><path d="M2 9h5.2 M2 15h5.2 M3.5 6 c2 2 2 10 0 12 M6 6 c2 2 2 10 0 12 c6 0 11-3 11-6 c0-3 -5-6 -11-6 Z M20.5 12h3.5" /><circle cx="19" cy="12" r="1.5" /></svg>
                </button>
              </div>
            </div>

            <div>
              <div className="tool-label">Others</div>
              <div className="toolbar-row">
                <button onClick={() => setTool('ammeter')} className={`tool-button icon-btn ${tool === 'ammeter' ? 'active' : ''}`} title="Ammeter">
                  <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" fill="none" strokeWidth="2"><circle cx="12" cy="12" r="9" /><path d="M9 16 l3 -8 l3 8 M10 13 h4" /></svg>
                </button>
                <button onClick={() => setTool('voltmeter')} className={`tool-button icon-btn ${tool === 'voltmeter' ? 'active' : ''}`} title="Voltmeter">
                  <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" fill="none" strokeWidth="2"><circle cx="12" cy="12" r="9" /><path d="M8 8 l4 8 l4 -8" /></svg>
                </button>
                <button onClick={() => setTool('acsource')} className={`tool-button icon-btn ${tool === 'acsource' ? 'active' : ''}`} title="AC Source">
                  <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" fill="none" strokeWidth="2"><circle cx="12" cy="12" r="9" /><path d="M7 12 c2-4 3 4 5 0 s3-4 5 0" /></svg>
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="tool-divider" />
        <button onClick={() => setIsMathExpanded(!isMathExpanded)} className="section-btn">
          <span>Mathematical Shapes</span>
          <span>{isMathExpanded ? '▼' : '▶'}</span>
        </button>

        {isMathExpanded && (
          <div className="toolbar-row" style={{ marginTop: '0.4rem' }}>
            <button onClick={() => setTool('tri_eq')} className={`tool-button icon-btn ${tool === 'tri_eq' ? 'active' : ''}`} title="Equilateral Triangle">
              <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" fill="none" strokeWidth="2"><path d="M12 4l9 16H3z" /></svg>
            </button>
            <button onClick={() => setTool('tri_right')} className={`tool-button icon-btn ${tool === 'tri_right' ? 'active' : ''}`} title="Right Triangle">
              <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" fill="none" strokeWidth="2"><path d="M6 4v16h14z" /></svg>
            </button>
            <button onClick={() => setTool('pentagon')} className={`tool-button icon-btn ${tool === 'pentagon' ? 'active' : ''}`} title="Pentagon">
              <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" fill="none" strokeWidth="2"><path d="M12 3l9 7-3 11H6l-3-11z" /></svg>
            </button>
            <button onClick={() => setTool('hexagon')} className={`tool-button icon-btn ${tool === 'hexagon' ? 'active' : ''}`} title="Hexagon">
              <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" fill="none" strokeWidth="2"><path d="M8 4h8l4 8-4 8H8l-4-8z" /></svg>
            </button>
            <button onClick={() => setTool('angle')} className={`tool-button icon-btn ${tool === 'angle' ? 'active' : ''}`} title="Angle Marker">
              <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" fill="none" strokeWidth="2"><path d="M18 18 L6 18 L18 6 M10 18 A 4 4 0 0 0 14 14" /></svg>
            </button>
          </div>
        )}

      </div>

      <div className="canvas-wrapper" onMouseDown={(e) => { if (e.target === e.currentTarget) setSelectedIds([]); }}>
        <svg
          ref={svgRef} viewBox="0 0 800 500" preserveAspectRatio="xMidYMid meet" overflow="visible"
          onMouseDown={startInteraction} onMouseMove={interact} onMouseUp={endInteraction} onMouseLeave={endInteraction}
          onTouchStart={startInteraction} onTouchMove={interact} onTouchEnd={endInteraction}
        >
          {showGrid && (
            <>
              <defs>
                <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
                  <path d="M 10 0 L 0 0 0 10" fill="none" className="grid-path" stroke="#bfbfbf" strokeWidth="0.5" />
                </pattern>
              </defs>
              <rect x="-5000" y="-5000" width="10000" height="10000" fill="url(#grid)" />
            </>
          )}

          {elements.map(el => renderElement(el, selectedIds.includes(el.id), hoveredId === el.id))}
          {tempElement && renderElement(tempElement, false, false)}

          {polylinePoints && (
            <g>
              <polyline
                points={polylinePoints.map(p => `${p.x},${p.y}`).join(' ')}
                fill="none" stroke={color} strokeWidth={penWidth} strokeLinecap="round" strokeLinejoin="round"
              />
              {polylinePoints.map((p, i) => (
                <circle key={`poly-p-${i}`} cx={p.x} cy={p.y} r="3" fill="white" stroke={color} strokeWidth="1" />
              ))}
            </g>
          )}

          {curvePoints && (
            <g>
              {curvePoints.length >= 2 && (() => {
                // Build the curve path for preview
                let pathData = `M ${curvePoints[0].x} ${curvePoints[0].y}`;
                if (curvePoints.length === 2) {
                  pathData += ` L ${curvePoints[1].x} ${curvePoints[1].y}`;
                } else {
                  for (let i = 1; i < curvePoints.length; i++) {
                    const controlX = curvePoints[i].x;
                    const controlY = curvePoints[i].y;
                    if (i < curvePoints.length - 1) {
                      const nextX = curvePoints[i + 1].x;
                      const nextY = curvePoints[i + 1].y;
                      pathData += ` Q ${controlX} ${controlY} ${nextX} ${nextY}`;
                      i++;
                    }
                  }
                }
                return <path d={pathData} fill="none" stroke={color} strokeWidth={penWidth} strokeLinecap="round" strokeLinejoin="round" />;
              })()}
              {curvePoints.map((p, i) => (
                <circle key={`curve-p-${i}`} cx={p.x} cy={p.y} r="3" fill="white" stroke={color} strokeWidth="1" />
              ))}
            </g>
          )}

          {selectedIds.length > 0 && !isKeyboardInputFocused && (() => {
            const bb = getGroupBoundingBox(selectedIds);
            if (!bb) return null;
            const isInteracting = dragging.active || resizing.active || rotating.active;
            
            const singleEl = selectedIds.length === 1 ? elements.find(e => e.id === selectedIds[0]) : null;
            const rotateVal = singleEl ? (singleEl.rotate || 0) : 0;
            const selectTransform = rotateVal ? `rotate(${rotateVal}, ${bb.cx}, ${bb.cy})` : '';

            return (
              <g transform={selectTransform}>
                {!isInteracting && (
                  <>
                    <rect
                      x={bb.x - 5}
                      y={bb.y - 5}
                      width={bb.width + 10}
                      height={bb.height + 10}
                      fill="transparent"
                      data-grab-target="true"
                      style={{ cursor: 'grab' }}
                    />
                    <rect x={bb.x - 5} y={bb.y - 5} width={bb.width + 10} height={bb.height + 10} fill="none" stroke="#6366f1" strokeWidth="1" strokeDasharray="4" pointerEvents="none" />
                    <line x1={bb.x + bb.width / 2} y1={bb.y - 5} x2={bb.x + bb.width / 2} y2={bb.y - 30} stroke="#6366f1" strokeWidth="1" strokeDasharray="4" />
                    {getHandles(bb).map(h => (
                      <circle
                        key={h.type}
                        cx={h.x}
                        cy={h.y}
                        r="3.5"
                        fill="white"
                        stroke="#6366f1"
                        strokeWidth="1"
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          if (h.type === 'rotate') {
                            const coords = getCoordinates(e);
                            const angle = Math.atan2(coords.y - bb.cy, coords.x - bb.cx) * (180 / Math.PI);
                            setRotating({ active: true, startAngle: angle });
                          } else {
                            setResizing({
                              active: true,
                              handle: h.type,
                              originalBB: bb,
                              originalElements: JSON.parse(JSON.stringify(elements))
                            });
                          }
                        }}
                        style={{ cursor: h.type === 'rotate' ? 'grab' : (h.type.includes('l') || h.type.includes('r') ? 'ew-resize' : 'ns-resize') }}
                      />
                    ))}
                  </>
                )}
                <line x1={bb.cx - 10} y1={bb.cy} x2={bb.cx + 10} y2={bb.cy} stroke="#6366f1" strokeWidth="1" />
                <line x1={bb.cx} y1={bb.cy - 10} x2={bb.cx} y2={bb.cy + 10} stroke="#6366f1" strokeWidth="1" />
              </g>
            );
          })()}

          {marquee && <rect x={marquee.x} y={marquee.y} width={marquee.width} height={marquee.height} fill="rgba(99, 102, 241, 0.1)" stroke="#6366f1" strokeWidth="1" strokeDasharray="4" />}
        </svg>
      </div>
      {renderLatexKeyboard()}
    </div>
  </div>
  );
};


export default SvgEditor;

